import { AppStatusTag } from '../components/AppStatusTag';
import { APP_TYPES } from '../appTypes';
import { useEffect, useId, useState } from 'react';

import { Alert, Button, FormField, Icon, Input, Switch, Tag } from '../ds';
import { LiveSessionTerminal, type SessionState as LiveSessionState } from '../components/LiveSessionTerminal';
import { DeploymentsPanel } from '../components/DeploymentsPanel';
import { ReadmePanel } from '../components/ReadmePanel';
import { AccessExplainer } from '../components/AccessExplainer';
import {
  AppServicePicker,
  DEFAULT_SCHEDULER_CRON,
  NO_APP_CAPABILITIES,
  SCHEDULER_TIME_ZONE,
  schedulerCronError,
} from '../components/AppServicePicker';
import { icons } from '../icons';
import { VibesIcon } from '../components/VibesIcon';
import {
  api,
  beginManualSignIn,
  relativeTime,
  type AppCapabilitySelection,
  type AppCloudSqlReadiness,
  type CloudSqlRuntimeReconciliation,
  type AppSecretSummary,
  type AppSummary,
  type AppSecretsStatus,
  type ApplicationAccess,
  type ApplicationAccessReadiness,
  type ApplicationAccessUpdate,
  type AppDetailsUpdate,
  type CatalogAccessUpdate,
  type SecretEnvironment,
} from '../api';
import { c, cardStyle, h1Style, mono, pageStyle } from '../tokens';
import './app-detail.css';

function AccessDelayNotice() {
  return <div className="access-delay-notice"><Icon name={icons.info} size="sm" color={c.teal} /><strong>Access changes take a few minutes to take effect.</strong></div>;
}

const TABS = ['Overview', 'Live Session', 'Deployments', 'Secrets', 'Docs', 'Settings'] as const;
export type AppDetailTab = (typeof TABS)[number];

const ENV_KEY_PATTERN = /^[A-Z][A-Z0-9_]{0,63}$/;
const CUSTOM_URL_PREFIX_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?$/;
const RESERVED_CUSTOM_URL_PREFIXES = new Set(['admin', 'api', 'portal', 'www']);
const CUSTOM_URL_SUFFIX = '.corp.abridge.com';
const SECRET_LANGUAGES = ['typescript-javascript', 'python', 'go'] as const;
type SecretLanguage = (typeof SECRET_LANGUAGES)[number];
type PendingSecretDraft = { updates: Record<string, string>; removals: string[] };
type AccessEnvironment = ApplicationAccessUpdate['environment'];

function emptyAccessForms(): Record<AccessEnvironment, ApplicationAccessUpdate> {
  return {
    dev: { environment: 'dev', members: [], allAbridgeUsers: false },
    prod: { environment: 'prod', members: [], allAbridgeUsers: false },
    both: { environment: 'both', members: [], allAbridgeUsers: false },
  };
}

function customUrlPrefixFrom(url: string | null): string {
  if (!url) return '';
  try {
    const hostname = new URL(url).hostname;
    return hostname.endsWith(CUSTOM_URL_SUFFIX)
      ? hostname.slice(0, -CUSTOM_URL_SUFFIX.length)
      : '';
  } catch {
    return '';
  }
}

const SECRET_SNIPPETS: Record<SecretLanguage, string> = {
  'typescript-javascript': `const apiKey = process.env.OPENAI_API_KEY;\n\nif (!apiKey) throw new Error('OPENAI_API_KEY is missing');`,
  python: `import os\n\napi_key = os.environ["OPENAI_API_KEY"]`,
  go: `apiKey := os.Getenv("OPENAI_API_KEY")\n\nif apiKey == "" {\n    log.Fatal("OPENAI_API_KEY is missing")\n}`,
};

interface AppDetailProps {
  repo: string;
  visible: boolean;
  selectedTab?: AppDetailTab;
  vibeCheckHref?: string;
  previewOnly?: boolean;
  currentUserLogin: string;
  isCdpAdmin: boolean;
  fromList: AppSummary | null;
  onBack: () => void;
  onArchived: () => void;
  onChanged: () => void;
  onTabChange: (tab: AppDetailTab) => void;
  onLiveSessionStateChange: (state: LiveSessionState) => void;
}

export function AppDetail({
  repo,
  visible,
  selectedTab,
  vibeCheckHref = '#/vibe-check',
  previewOnly = false,
  currentUserLogin,
  isCdpAdmin,
  fromList,
  onBack,
  onArchived,
  onChanged,
  onTabChange,
  onLiveSessionStateChange,
}: AppDetailProps) {
  // Seed from the catalog row so the header paints immediately, then refresh.
  const [app, setApp] = useState<AppSummary | null>(fromList);
  const [error, setError] = useState<string | null>(null);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [archiving, setArchiving] = useState(false);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [archiveConfirmText, setArchiveConfirmText] = useState('');
  const [archivedAction, setArchivedAction] = useState<'restore' | 'delete' | null>(null);
  const [archivedActionBusy, setArchivedActionBusy] = useState(false);
  const [archivedActionError, setArchivedActionError] = useState<string | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletionPullRequestUrl, setDeletionPullRequestUrl] = useState<string | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [detailsSaving, setDetailsSaving] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [detailsForm, setDetailsForm] = useState<AppDetailsUpdate>({ title: '', description: '' });
  const [ownersModalOpen, setOwnersModalOpen] = useState(false);
  const [ownersSaving, setOwnersSaving] = useState(false);
  const [ownersError, setOwnersError] = useState<string | null>(null);
  const [ownersForm, setOwnersForm] = useState<CatalogAccessUpdate>({
    visibility: 'private',
    owner: '',
    ownerUsers: [],
    ownerGroups: [],
  });
  const [ownerUserDraft, setOwnerUserDraft] = useState('');
  const [ownerGroupDraft, setOwnerGroupDraft] = useState('');
  const [ownerUserSuggestions, setOwnerUserSuggestions] = useState<string[]>([]);
  const [ownerUserSuggestionsLoading, setOwnerUserSuggestionsLoading] = useState(false);
  const [ownerUserSuggestionsError, setOwnerUserSuggestionsError] = useState<string | null>(null);
  const [ownerGroupSuggestions, setOwnerGroupSuggestions] = useState<{ slug: string; name: string }[]>([]);
  const [ownerGroupSuggestionsLoading, setOwnerGroupSuggestionsLoading] = useState(false);
  const [ownerGroupSuggestionsError, setOwnerGroupSuggestionsError] = useState<string | null>(null);
  const [transferStep, setTransferStep] = useState<1 | 2 | 3 | null>(null);
  const [transferNewOwner, setTransferNewOwner] = useState('');
  const [transferOwnerValidated, setTransferOwnerValidated] = useState(false);
  const [transferSuggestions, setTransferSuggestions] = useState<string[]>([]);
  const [transferSuggestionsLoading, setTransferSuggestionsLoading] = useState(false);
  const [transferSuggestionsError, setTransferSuggestionsError] = useState<string | null>(null);
  const [keepCurrentOwner, setKeepCurrentOwner] = useState(false);
  const [transferAccepted, setTransferAccepted] = useState(false);
  const [transferSaving, setTransferSaving] = useState(false);
  const [transferError, setTransferError] = useState<string | null>(null);
  const [visibilityDraft, setVisibilityDraft] = useState<'private' | 'public'>('private');
  const [visibilitySaving, setVisibilitySaving] = useState(false);
  const [visibilityError, setVisibilityError] = useState<string | null>(null);
  const [enabledCapabilities, setEnabledCapabilities] = useState<AppCapabilitySelection>(NO_APP_CAPABILITIES);
  const [pendingCapabilities, setPendingCapabilities] = useState<AppCapabilitySelection>(NO_APP_CAPABILITIES);
  const [capabilityDraft, setCapabilityDraft] = useState<AppCapabilitySelection>(NO_APP_CAPABILITIES);
  const [schedulerCron, setSchedulerCron] = useState(DEFAULT_SCHEDULER_CRON);
  const [persistedSchedulerCron, setPersistedSchedulerCron] = useState(DEFAULT_SCHEDULER_CRON);
  const [schedulerTimeZone, setSchedulerTimeZone] = useState(SCHEDULER_TIME_ZONE);
  const [capabilitiesLoading, setCapabilitiesLoading] = useState(true);
  const [capabilitiesSaving, setCapabilitiesSaving] = useState(false);
  const [capabilitiesError, setCapabilitiesError] = useState<string | null>(null);
  const [capabilitiesPullRequestUrl, setCapabilitiesPullRequestUrl] = useState<string | null>(null);
  const [capabilityChangePending, setCapabilityChangePending] = useState(false);
  const [devOnly, setDevOnly] = useState(false);
  const [proposedDevOnly, setProposedDevOnly] = useState(false);
  const [environmentChangePending, setEnvironmentChangePending] = useState(false);
  const [environmentSaving, setEnvironmentSaving] = useState(false);
  const [environmentError, setEnvironmentError] = useState<string | null>(null);
  const [environmentModalOpen, setEnvironmentModalOpen] = useState(false);
  const [environmentTargetDevOnly, setEnvironmentTargetDevOnly] = useState(false);
  const [environmentReason, setEnvironmentReason] = useState('');
  const [environmentConfirmText, setEnvironmentConfirmText] = useState('');

  useEffect(() => {
    if (devOnly || proposedDevOnly) setAccessEnvironment('dev');
  }, [devOnly, proposedDevOnly]);
  const [cloudSqlReadiness, setCloudSqlReadiness] = useState<AppCloudSqlReadiness | null>(null);
  const [cloudSqlReadinessError, setCloudSqlReadinessError] = useState<string | null>(null);
  const [cloudSqlRuntimeBusy, setCloudSqlRuntimeBusy] = useState<SecretEnvironment | null>(null);
  const [cloudSqlRuntimeResults, setCloudSqlRuntimeResults] = useState<Partial<Record<SecretEnvironment, CloudSqlRuntimeReconciliation>>>({});
  const [cloudSqlRuntimeErrors, setCloudSqlRuntimeErrors] = useState<Partial<Record<SecretEnvironment, string>>>({});
  const [iapEnabled, setIapEnabled] = useState(true);
  const [proposedIapEnabled, setProposedIapEnabled] = useState(true);
  const [iapChangePending, setIapChangePending] = useState(false);
  const [iapExceptionJustification, setIapExceptionJustification] = useState<string | null>(null);
  const [iapHelpOpen, setIapHelpOpen] = useState(false);
  const [iapRequestModalOpen, setIapRequestModalOpen] = useState(false);
  const [iapJustificationDraft, setIapJustificationDraft] = useState('');
  const [iapSaving, setIapSaving] = useState(false);
  const [iapError, setIapError] = useState<string | null>(null);
  const [applicationAccess, setApplicationAccess] = useState<ApplicationAccess>({
    both: [],
    dev: [],
    prod: [],
    allAbridgeUsers: { dev: false, prod: false },
  });
  const [accessModalOpen, setAccessModalOpen] = useState(false);
  const [accessSaving, setAccessSaving] = useState(false);
  const [accessError, setAccessError] = useState<string | null>(null);
  const [accessForms, setAccessForms] = useState<Record<AccessEnvironment, ApplicationAccessUpdate>>(
    emptyAccessForms,
  );
  const [accessDrafts, setAccessDrafts] = useState<Record<AccessEnvironment, string>>({
    dev: '',
    prod: '',
    both: '',
  });
  const [accessEnvironment, setAccessEnvironment] = useState<AccessEnvironment>('both');
  const accessForm = accessForms[accessEnvironment];
  const accessDraft = accessDrafts[accessEnvironment];
  const [accessReadiness, setAccessReadiness] = useState<ApplicationAccessReadiness>({
    dev: { ready: false, iapEnabled: null },
    prod: { ready: false, iapEnabled: null },
  });
  const [accessReadinessLoading, setAccessReadinessLoading] = useState(true);
  const [tab, setTab] = useState<AppDetailTab>(selectedTab ?? 'Overview');
  const [liveSessionState, setLiveSessionState] = useState<LiveSessionState>('idle');
  const [cloudRunUrlCopied, setCloudRunUrlCopied] = useState<'dev' | 'prod' | 'prod-original' | null>(null);
  const [customUrlModalOpen, setCustomUrlModalOpen] = useState(false);
  const [customUrlPrefix, setCustomUrlPrefix] = useState('');
  const [customUrlEnvironment, setCustomUrlEnvironment] = useState<'dev' | 'prod'>('prod');
  const [customUrlAvailability, setCustomUrlAvailability] = useState<'idle' | 'checking' | 'available' | 'unavailable'>('idle');

  useEffect(() => {
    api
      .app(repo)
      .then(({ app: fetched }) => setApp(fetched))
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : 'Could not load this app.'),
      );
  }, [repo]);

  useEffect(() => {
    if (visible && selectedTab) setTab(selectedTab);
  }, [selectedTab, visible]);

  useEffect(() => {
    if (!customUrlModalOpen) return;
    const prefix = customUrlPrefix.trim();
    if (!CUSTOM_URL_PREFIX_PATTERN.test(prefix)) {
      setCustomUrlAvailability('idle');
      return;
    }

    setCustomUrlAvailability('checking');
    const check = window.setTimeout(() => {
      setCustomUrlAvailability(RESERVED_CUSTOM_URL_PREFIXES.has(prefix) ? 'unavailable' : 'available');
    }, 600);
    return () => window.clearTimeout(check);
  }, [customUrlModalOpen, customUrlPrefix]);

  useEffect(() => {
    if (app) setVisibilityDraft(app.visibility);
  }, [app?.visibility]);

  useEffect(() => {
    const available = enabledCapabilities.secretsManagement || pendingCapabilities.secretsManagement;
    if (!capabilitiesLoading && !available && tab === 'Secrets') {
      setTab('Overview');
      if (visible) onTabChange('Overview');
    }
  }, [capabilitiesLoading, enabledCapabilities.secretsManagement, pendingCapabilities.secretsManagement, onTabChange, tab, visible]);

  useEffect(() => {
    if (!app?.archived) return;
    if (tab === 'Live Session' || tab === 'Secrets') {
      setTab('Overview');
      if (visible) onTabChange('Overview');
    }
    setOwnersModalOpen(false);
    setAccessModalOpen(false);
    setIapRequestModalOpen(false);
    setTransferStep(null);
    setArchiveModalOpen(false);
  }, [app?.archived, onTabChange, tab, visible]);

  useEffect(() => {
    if (!app?.canManage || app.archived) {
      setApplicationAccess({ both: [], dev: [], prod: [], allAbridgeUsers: { dev: false, prod: false } });
      setAccessReadiness({
        dev: { ready: false, iapEnabled: null },
        prod: { ready: false, iapEnabled: null },
      });
      setAccessReadinessLoading(false);
      return;
    }
    setAccessReadinessLoading(true);
    setAccessReadiness({
      dev: { ready: false, iapEnabled: null },
      prod: { ready: false, iapEnabled: null },
    });
    api.applicationAccess(repo)
      .then(setApplicationAccess)
      .catch((err: unknown) => {
        console.warn('[portal] Application access lookup failed', { repo, detail: err });
      });
    api.applicationAccessReadiness(repo)
      .then(setAccessReadiness)
      .catch((err: unknown) => {
        console.warn('[portal] Application access readiness lookup failed', { repo, detail: err });
      })
      .finally(() => setAccessReadinessLoading(false));
  }, [repo, app?.archived, app?.canManage, app?.visibility]);

  useEffect(() => {
    let cancelled = false;
    setCapabilitiesLoading(true);
    setCapabilitiesError(null);
    setEnabledCapabilities(NO_APP_CAPABILITIES);
    setPendingCapabilities(NO_APP_CAPABILITIES);
    setCapabilityDraft(NO_APP_CAPABILITIES);
    setSchedulerCron(DEFAULT_SCHEDULER_CRON);
    setPersistedSchedulerCron(DEFAULT_SCHEDULER_CRON);
    setSchedulerTimeZone(SCHEDULER_TIME_ZONE);
    setCapabilitiesPullRequestUrl(null);
    setCapabilityChangePending(false);
    setDevOnly(false);
    setProposedDevOnly(false);
    setEnvironmentChangePending(false);
    setEnvironmentError(null);
    setIapEnabled(true);
    setProposedIapEnabled(true);
    setIapChangePending(false);
    setIapExceptionJustification(null);
    api
      .capabilitySettings(repo)
      .then(settings => {
        if (cancelled) return;
        setEnabledCapabilities(settings.enabled);
        setDevOnly(settings.devOnly);
        setProposedDevOnly(settings.proposedDevOnly);
        setEnvironmentChangePending(settings.environmentChangePending);
        setPendingCapabilities(settings.pending);
        setCapabilityDraft({
          secretsManagement: settings.enabled.secretsManagement || settings.pending.secretsManagement,
          cloudRunJobs: settings.enabled.cloudRunJobs || settings.pending.cloudRunJobs,
          cloudScheduler: settings.enabled.cloudScheduler || settings.pending.cloudScheduler,
          cloudStorage: settings.enabled.cloudStorage || settings.pending.cloudStorage,
          bigquery: settings.enabled.bigquery || settings.pending.bigquery,
          cloudSql: settings.enabled.cloudSql || settings.pending.cloudSql,
        });
        setSchedulerCron(settings.schedulerCron);
        setPersistedSchedulerCron(settings.schedulerCron);
        setSchedulerTimeZone(settings.schedulerTimeZone);
        setCapabilitiesPullRequestUrl(settings.pullRequestUrl);
        setCapabilityChangePending(
          Object.values(settings.pending).some(Boolean) || settings.schedulerChangePending,
        );
        setIapEnabled(settings.iapEnabled);
        setProposedIapEnabled(settings.proposedIapEnabled);
        setIapChangePending(settings.iapChangePending);
        setIapExceptionJustification(settings.iapExceptionJustification);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setCapabilitiesError(err instanceof Error ? err.message : 'Could not load capability settings.');
      })
      .finally(() => {
        if (!cancelled) setCapabilitiesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [repo]);

  useEffect(() => {
    if (!app?.canManage || app.archived || !enabledCapabilities.cloudSql) {
      setCloudSqlReadiness(null);
      setCloudSqlReadinessError(null);
      return;
    }

    let cancelled = false;
    let timeout: number | undefined;
    const refresh = (): void => {
      api.cloudSqlReadiness(repo)
        .then(readiness => {
          if (cancelled) return;
          setCloudSqlReadiness(readiness);
          setCloudSqlReadinessError(null);
          if (readiness.state !== 'ready') {
            timeout = window.setTimeout(refresh, readiness.state === 'provisioning' ? 10_000 : 30_000);
          }
        })
        .catch((error: unknown) => {
          if (cancelled) return;
          setCloudSqlReadiness(null);
          setCloudSqlReadinessError(
            error instanceof Error ? error.message : 'Could not check Cloud SQL readiness.',
          );
          timeout = window.setTimeout(refresh, 30_000);
        });
    };

    setCloudSqlReadiness(null);
    setCloudSqlReadinessError(null);
    refresh();
    return () => {
      cancelled = true;
      if (timeout !== undefined) window.clearTimeout(timeout);
    };
  }, [repo, app?.archived, app?.canManage, enabledCapabilities.cloudSql]);

  const reconcileCloudSqlRuntime = (environment: SecretEnvironment) => {
    setCloudSqlRuntimeBusy(environment);
    setCloudSqlRuntimeResults(current => ({ ...current, [environment]: undefined }));
    setCloudSqlRuntimeErrors(current => ({ ...current, [environment]: undefined }));
    api.reconcileCloudSqlRuntime(repo, environment)
      .then(result => {
        setCloudSqlRuntimeResults(current => ({ ...current, [environment]: result }));
        setCloudSqlReadiness(current => current ? {
          ...current,
          environments: current.environments.map(item => item.environment === environment ? {
            ...item,
            runtimeConfiguration: { service: true, job: result.job ? true : null, ready: true },
          } : item),
        } : current);
      })
      .catch((error: unknown) => {
        setCloudSqlRuntimeErrors(current => ({
          ...current,
          [environment]: error instanceof Error ? error.message : 'Could not apply the runtime contract.',
        }));
      })
      .finally(() => setCloudSqlRuntimeBusy(current => current === environment ? null : current));
  };

  useEffect(() => {
    if (!ownersModalOpen || ownerUserDraft.trim().length < 2) {
      setOwnerUserSuggestions([]);
      setOwnerUserSuggestionsLoading(false);
      setOwnerUserSuggestionsError(null);
      return;
    }

    let cancelled = false;
    setOwnerUserSuggestionsLoading(true);
    setOwnerUserSuggestionsError(null);
    const timeout = window.setTimeout(() => {
      api
        .orgMembers(ownerUserDraft)
        .then(({ users }) => {
          if (cancelled) return;
          const existing = new Set(ownersForm.ownerUsers);
          setOwnerUserSuggestions(
            users.map(user => user.login).filter(login => !existing.has(login.toLowerCase())),
          );
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          setOwnerUserSuggestions([]);
          setOwnerUserSuggestionsError(
            err instanceof Error ? err.message : 'Could not load organization members.',
          );
        })
        .finally(() => {
          if (!cancelled) setOwnerUserSuggestionsLoading(false);
        });
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [ownerUserDraft, ownersForm.ownerUsers, ownersModalOpen]);

  useEffect(() => {
    if (transferStep !== 1 || transferNewOwner.trim().length < 2 || transferOwnerValidated) {
      setTransferSuggestions([]);
      setTransferSuggestionsLoading(false);
      setTransferSuggestionsError(null);
      return;
    }

    let cancelled = false;
    setTransferSuggestionsLoading(true);
    setTransferSuggestionsError(null);
    const timeout = window.setTimeout(() => {
      api.orgMembers(transferNewOwner).then(({ users }) => {
        if (cancelled) return;
        setTransferSuggestions(
          users.map(user => user.login).filter(login => login.toLowerCase() !== app?.primaryOwner?.toLowerCase()),
        );
      }).catch((err: unknown) => {
        if (cancelled) return;
        setTransferSuggestions([]);
        setTransferSuggestionsError(err instanceof Error ? err.message : 'Could not load organization members.');
      }).finally(() => {
        if (!cancelled) setTransferSuggestionsLoading(false);
      });
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [app?.primaryOwner, transferNewOwner, transferOwnerValidated, transferStep]);

  useEffect(() => {
    if (!ownersModalOpen || ownerGroupDraft.trim().length < 2) {
      setOwnerGroupSuggestions([]);
      setOwnerGroupSuggestionsLoading(false);
      setOwnerGroupSuggestionsError(null);
      return;
    }

    let cancelled = false;
    setOwnerGroupSuggestionsLoading(true);
    setOwnerGroupSuggestionsError(null);
    const timeout = window.setTimeout(() => {
      api
        .orgTeams(ownerGroupDraft)
        .then(({ teams }) => {
          if (cancelled) return;
          const existing = new Set(ownersForm.ownerGroups);
          setOwnerGroupSuggestions(teams.filter(team => !existing.has(team.slug.toLowerCase())));
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          setOwnerGroupSuggestions([]);
          setOwnerGroupSuggestionsError(
            err instanceof Error ? err.message : 'Could not load organization teams.',
          );
        })
        .finally(() => {
          if (!cancelled) setOwnerGroupSuggestionsLoading(false);
        });
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [ownerGroupDraft, ownersForm.ownerGroups, ownersModalOpen]);

  const cloudRunUrl = app?.readiness.devUrl ?? null;
  const prodCloudRunUrl = app?.readiness.prodUrl ?? null;
  const customProdUrl = app?.readiness.customProdUrl ?? null;
  const displayedProdUrl = customProdUrl ?? prodCloudRunUrl;
  const readinessUnavailable = app?.readiness.state === 'unavailable';
  const canManage = Boolean(app?.canManage && !app.archived);
  const secretsAvailable = enabledCapabilities.secretsManagement || pendingCapabilities.secretsManagement;
  const tabs = TABS.filter(label => {
    if (app?.archived && (label === 'Live Session' || label === 'Secrets')) return false;
    return label !== 'Secrets' || secretsAvailable;
  });
  const accessReady = accessEnvironment === 'both'
    ? accessReadiness.dev.ready && accessReadiness.prod.ready
    : accessReadiness[accessEnvironment].ready;
  const liveIapStates = [accessReadiness.dev, accessReadiness.prod]
    .filter(environment => environment.ready)
    .map(environment => environment.iapEnabled);
  const iapAccessUnavailable = liveIapStates.some(enabled => enabled === false);
  const isPublic = app?.visibility === 'public';
  const appAccessUnavailable = isPublic || iapAccessUnavailable;
  const iapDeploymentPending = !iapChangePending
    && liveIapStates.length === 2
    && liveIapStates.some(enabled => enabled !== iapEnabled);
  const archiveConfirmed = Boolean(app && archiveConfirmText === app.repo);
  const deleteConfirmed = Boolean(app && deleteConfirmText === app.repo);
  const isArchivedAdmin = Boolean(app?.archived && isCdpAdmin);
  const isCurrentPrimaryOwner = canManage && app?.primaryOwner?.toLowerCase() === currentUserLogin.toLowerCase();
  const canTransferOwnership = Boolean(app?.primaryOwner && canManage && (isCurrentPrimaryOwner || isCdpAdmin));

  const openDetailsModal = (): void => {
    if (!app || !canManage) return;
    setDetailsForm({ title: app.title, description: app.description, appType: app.appType ?? null });
    setDetailsError(null);
    setDetailsModalOpen(true);
  };

  const saveDetails = async (): Promise<void> => {
    if (!app || !canManage || detailsSaving) return;

    setDetailsSaving(true);
    setDetailsError(null);
    try {
      const { app: updated } = await api.updateAppDetails(app.repo, {
        title: detailsForm.title.trim(),
        description: detailsForm.description.trim(),
        ...((detailsForm.appType ?? null) !== (app.appType ?? null) ? { appType: detailsForm.appType ?? null } : {}),
      });
      setApp(updated);
      setDetailsModalOpen(false);
      onChanged();
    } catch (err) {
      setDetailsError(err instanceof Error ? err.message : 'Could not update this app\u2019s details.');
    } finally {
      setDetailsSaving(false);
    }
  };

  const openOwnersModal = (): void => {
    if (!app || !canManage) return;
    setOwnersForm({
      visibility: app.visibility,
      owner: app.owner,
      ownerUsers: app.ownerUsers,
      ownerGroups: [...new Set(['cdp-admins', ...app.ownerGroups])],
    });
    setOwnerUserDraft('');
    setOwnerGroupDraft('');
    setOwnerGroupSuggestions([]);
    setOwnersError(null);
    setOwnersModalOpen(true);
  };

  const addOwnerValue = (field: 'ownerUsers' | 'ownerGroups', value: string): void => {
    const next = value.trim().toLowerCase();
    if (!next) return;

    setOwnersForm(current => ({
      ...current,
      [field]: current[field].includes(next) ? current[field] : [...current[field], next],
    }));
    if (field === 'ownerUsers') {
      setOwnerUserDraft('');
      setOwnerUserSuggestions([]);
    } else {
      setOwnerGroupDraft('');
      setOwnerGroupSuggestions([]);
    }
  };

  const removeOwnerValue = (field: 'ownerUsers' | 'ownerGroups', value: string): void => {
    setOwnersForm(current => ({
      ...current,
      [field]: current[field].filter(candidate => candidate !== value),
    }));
  };

  const saveOwners = async (): Promise<void> => {
    if (!app || !canManage || ownersSaving) return;

    setOwnersSaving(true);
    setOwnersError(null);
    try {
      const { app: updated } = await api.updateCatalogAccess(app.repo, {
        ...ownersForm,
        owner: app.owner,
        ownerGroups: ownersForm.ownerGroups,
      });
      setApp(updated);
      setOwnersModalOpen(false);
    } catch (err) {
      setOwnersError(err instanceof Error ? err.message : 'Could not update owners.');
    } finally {
      setOwnersSaving(false);
    }
  };

  const saveCatalogVisibility = async (): Promise<void> => {
    if (!app || !canManage || visibilitySaving || visibilityDraft === app.visibility) return;

    setVisibilitySaving(true);
    setVisibilityError(null);
    try {
      const { app: updated } = await api.updateCatalogAccess(app.repo, {
        visibility: visibilityDraft,
        owner: app.owner,
        ownerUsers: app.ownerUsers,
        ownerGroups: app.ownerGroups,
      });
      setApp(updated);
    } catch (err) {
      setVisibilityError(err instanceof Error ? err.message : 'Could not update catalog visibility.');
    } finally {
      setVisibilitySaving(false);
    }
  };

  const openTransferModal = (): void => {
    if (!canTransferOwnership) return;
    setTransferNewOwner('');
    setTransferOwnerValidated(false);
    setTransferSuggestions([]);
    setKeepCurrentOwner(false);
    setTransferAccepted(false);
    setTransferError(null);
    setTransferStep(1);
  };

  const continueTransfer = (): void => {
    if (!app || !transferOwnerValidated) return;
    const alreadyAdditional = app.ownerUsers.includes(app.primaryOwner ?? '');
    if (alreadyAdditional) {
      setKeepCurrentOwner(true);
      setTransferStep(3);
    } else {
      setTransferStep(2);
    }
  };

  const transferOwnership = async (): Promise<void> => {
    if (!app || !canTransferOwnership || transferSaving || !transferAccepted || !transferOwnerValidated) return;
    setTransferSaving(true);
    setTransferError(null);
    try {
      const { app: updated } = await api.transferOwnership(app.repo, {
        newOwner: transferNewOwner,
        keepCurrentOwner,
      });
      setApp(updated);
      setTransferStep(null);
    } catch (err) {
      setTransferError(err instanceof Error ? err.message : 'Could not transfer app ownership.');
    } finally {
      setTransferSaving(false);
    }
  };

  const hasUnsavedCapabilities = !capabilitiesLoading && (
    (Object.keys(capabilityDraft) as (keyof AppCapabilitySelection)[]).some(
      key => capabilityDraft[key] && !enabledCapabilities[key] && !pendingCapabilities[key],
    ) || (capabilityDraft.cloudScheduler && schedulerCron !== persistedSchedulerCron)
  );

  const saveCapabilityEnablement = async (): Promise<void> => {
    if (!app || !canManage || capabilitiesSaving) return;
    setCapabilitiesSaving(true);
    setCapabilitiesError(null);
    try {
      const result = await api.requestCapabilityEnablement(app.repo, capabilityDraft, schedulerCron);
      setEnabledCapabilities(result.enabled);
      setPendingCapabilities(result.pending);
      setCapabilityDraft({
        secretsManagement: result.enabled.secretsManagement || result.pending.secretsManagement,
        cloudRunJobs: result.enabled.cloudRunJobs || result.pending.cloudRunJobs,
        cloudScheduler: result.enabled.cloudScheduler || result.pending.cloudScheduler,
        cloudStorage: result.enabled.cloudStorage || result.pending.cloudStorage,
        bigquery: result.enabled.bigquery || result.pending.bigquery,
        cloudSql: result.enabled.cloudSql || result.pending.cloudSql,
      });
      setSchedulerCron(result.schedulerCron);
      setPersistedSchedulerCron(result.schedulerCron);
      setSchedulerTimeZone(result.schedulerTimeZone);
      setCapabilitiesPullRequestUrl(result.pullRequestUrl);
      setCapabilityChangePending(
        Object.values(result.pending).some(Boolean) || result.schedulerChangePending,
      );
      setIapEnabled(result.iapEnabled);
      setProposedIapEnabled(result.proposedIapEnabled);
      setIapChangePending(result.iapChangePending);
      setIapExceptionJustification(result.iapExceptionJustification);
    } catch (err) {
      setCapabilitiesError(err instanceof Error ? err.message : 'Could not request capability changes.');
    } finally {
      setCapabilitiesSaving(false);
    }
  };

  const requestIapChange = async (enabled: boolean, justification?: string): Promise<void> => {
    if (!app || !canManage || iapSaving || iapChangePending) return;
    setIapSaving(true);
    setIapError(null);
    try {
      const result = await api.requestIapChange(app.repo, enabled, justification);
      setIapEnabled(result.iapEnabled);
      setProposedIapEnabled(result.proposedIapEnabled);
      setIapChangePending(result.iapChangePending);
      setIapExceptionJustification(result.iapExceptionJustification);
      setCapabilitiesPullRequestUrl(result.pullRequestUrl);
      setIapRequestModalOpen(false);
      setIapJustificationDraft('');
    } catch (err) {
      setIapError(err instanceof Error ? err.message : 'Could not request the IAP change.');
    } finally {
      setIapSaving(false);
    }
  };

  const openAccessModal = (): void => {
    if (!canManage || appAccessUnavailable) return;
    const allAbridgeUsers = applicationAccess.allAbridgeUsers.dev && applicationAccess.allAbridgeUsers.prod;
    const environment = allAbridgeUsers ? 'both' : 'dev';
    setAccessForms({
      dev: {
        environment: 'dev',
        members: applicationAccess.dev,
        allAbridgeUsers: applicationAccess.allAbridgeUsers.dev,
      },
      prod: {
        environment: 'prod',
        members: applicationAccess.prod,
        allAbridgeUsers: applicationAccess.allAbridgeUsers.prod,
      },
      both: {
        environment: 'both',
        members: applicationAccess.both,
        allAbridgeUsers,
      },
    });
    setAccessEnvironment(environment);
    setAccessDrafts({ dev: '', prod: '', both: '' });
    setAccessError(null);
    setAccessModalOpen(true);
  };

  const selectAccessEnvironment = (environment: AccessEnvironment): void => {
    setAccessEnvironment(environment);
    setAccessError(null);
  };

  const setAccessDraft = (value: string): void => {
    setAccessDrafts(current => ({ ...current, [accessEnvironment]: value }));
  };

  const updateAccessForm = (
    update: (current: ApplicationAccessUpdate) => ApplicationAccessUpdate,
  ): void => {
    setAccessForms(current => ({
      ...current,
      [accessEnvironment]: update(current[accessEnvironment]),
    }));
  };

  const setAllAbridgeUsers = (enabled: boolean): void => {
    if (enabled) {
      setAccessForms(current => ({
        ...current,
        both: { environment: 'both', members: [], allAbridgeUsers: true },
      }));
      setAccessEnvironment('both');
    } else {
      updateAccessForm(current => ({ ...current, allAbridgeUsers: false }));
    }
    setAccessError(null);
  };

  const addAccessMember = (): void => {
    const member = accessDraft.trim().toLowerCase();
    if (!/^[^@\s]+@abridge\.com$/.test(member)) {
      setAccessError('Enter a valid @abridge.com email address.');
      return;
    }
    updateAccessForm(current => ({
      ...current,
      members: current.members.includes(member) ? current.members : [...current.members, member],
    }));
    setAccessDraft('');
    setAccessError(null);
  };

  const saveApplicationAccess = async (): Promise<void> => {
    if (!app || !canManage || accessSaving || appAccessUnavailable) return;
    setAccessSaving(true);
    setAccessError(null);
    try {
      const updated = await api.updateApplicationAccess(app.repo, accessForm);
      setApplicationAccess(updated);
      setAccessModalOpen(false);
    } catch (err) {
      setAccessError(err instanceof Error ? err.message : 'Could not update application access.');
    } finally {
      setAccessSaving(false);
    }
  };

  const submitEnvironmentMode = async (): Promise<void> => {
    if (!app || !isCdpAdmin || !canManage || environmentSaving || environmentChangePending
      || environmentConfirmText !== app.repo || environmentReason.trim().length < 20
      || environmentReason.trim().length > 2000) return;
    setEnvironmentSaving(true);
    setEnvironmentError(null);
    try {
      const result = await api.requestEnvironmentMode(
        app.repo, environmentTargetDevOnly, environmentConfirmText, environmentReason,
      );
      setDevOnly(result.devOnly);
      setProposedDevOnly(result.proposedDevOnly);
      setEnvironmentChangePending(result.environmentChangePending);
      setCapabilitiesPullRequestUrl(result.pullRequestUrl);
      setEnvironmentModalOpen(false);
    } catch (err) {
      setEnvironmentError(err instanceof Error ? err.message : 'Could not request the environment change.');
    } finally {
      setEnvironmentSaving(false);
    }
  };

  const archiveApp = async (): Promise<void> => {
    if (!app || app.archived || !canManage || archiving || !archiveConfirmed) return;

    setArchiving(true);
    setArchiveError(null);
    try {
      const { app: archivedApp } = await api.archiveApp(app.repo);
      setApp(archivedApp);
      onArchived();
    } catch (err) {
      setArchiveError(err instanceof Error ? err.message : 'Could not archive this app.');
      setArchiving(false);
    }
  };

  const restoreApp = async (): Promise<void> => {
    if (!app || !isArchivedAdmin || archivedActionBusy) return;
    setArchivedActionBusy(true);
    setArchivedActionError(null);
    try {
      const { app: restoredApp } = await api.restoreApp(app.repo);
      setApp(restoredApp);
      setArchivedAction(null);
      onChanged();
    } catch (err) {
      setArchivedActionError(err instanceof Error ? err.message : 'Could not restore this app.');
    } finally {
      setArchivedActionBusy(false);
    }
  };

  const requestPermanentDeletion = async (): Promise<void> => {
    if (!app || !isArchivedAdmin || archivedActionBusy || !deleteConfirmed) return;
    setArchivedActionBusy(true);
    setArchivedActionError(null);
    try {
      const { pullRequest } = await api.requestPermanentDeletion(app.repo, deleteConfirmText);
      setDeletionPullRequestUrl(pullRequest.url);
      setArchivedAction(null);
      setDeleteConfirmText('');
    } catch (err) {
      setArchivedActionError(err instanceof Error ? err.message : 'Could not queue permanent deletion.');
    } finally {
      setArchivedActionBusy(false);
    }
  };

  const copyAppUrl = (url: string, key: 'dev' | 'prod' | 'prod-original'): void => {
    void navigator.clipboard.writeText(url).then(() => {
      setCloudRunUrlCopied(key);
      window.setTimeout(() => setCloudRunUrlCopied(current => current === key ? null : current), 1800);
    });
  };

  return (
    <div
      style={{
        ...pageStyle,
        gap: 24,
        width: '100%',
        boxSizing: 'border-box',
        maxWidth: tab === 'Live Session' ? 'none' : pageStyle.maxWidth,
      }}
    >
      <button
        type="button"
        onClick={onBack}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          background: 'none',
          border: 0,
          padding: 0,
          fontFamily: 'inherit',
          fontSize: 13,
          color: c.textMuted,
          cursor: 'pointer',
          width: 'fit-content',
        }}
      >
        <Icon name={icons.arrowLeft} size="sm" color={c.textMuted} />
        <span>Your apps</span>
      </button>

      {error && (
        <Alert variant="danger" showIcon>
          <span style={{ fontSize: 14, lineHeight: 1.35 }}>
            {error}
            {error === 'Sign in with GitHub first.' && (
              <>
                {' '}
                <button
                  type="button"
                  onClick={beginManualSignIn}
                  style={{
                    border: 0,
                    padding: 0,
                    background: 'none',
                    color: c.brandLink,
                    font: 'inherit',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                >
                  Sign in again
                </button>
              </>
            )}
          </span>
        </Alert>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 24,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h1 style={h1Style}>{app?.title ?? repo}</h1>
            {canManage && (
              <Button
                variant="icon"
                size="sm"
                aria-label="Edit app name and description"
                onPress={openDetailsModal}
              >
                <Icon name={icons.edit} size="sm" color={c.textMuted} />
              </Button>
            )}
            {app?.archived && <Tag variant="default">Archived</Tag>}
          </div>
          <p style={{ margin: 0, fontSize: 15, lineHeight: 1.5, color: c.textBody }}>
            {app?.description ?? ' '}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flex: '0 0 auto', whiteSpace: 'nowrap' }}>
          <Button
            variant="outlined"
            size="sm"
            leftIcon={icons.openInNew}
            onPress={() => {
              if (app) window.open(app.htmlUrl, '_blank', 'noreferrer');
            }}
          >
            Open repo
          </Button>
          {(canManage || isArchivedAdmin) && (
            <Button
              variant="secondary"
              size="sm"
              leftIcon={icons.settings}
              onPress={() => {
                setTab('Settings');
                onTabChange('Settings');
              }}
            >
              Manage
            </Button>
          )}
        </div>
      </div>

      {app?.archived && (
        <Alert variant="warning" showIcon>
          <span style={{ fontSize: 14, lineHeight: 1.45 }}>
            This app is archived and read-only. All of its managed infrastructure will be
            decommissioned through a reviewed pull request. A cdp-admins member can restore the app
            or queue permanent repository deletion from Settings.
            {app.decommissionPullRequest && (
              <> <a href={app.decommissionPullRequest.url} target="_blank" rel="noreferrer">View teardown PR #{app.decommissionPullRequest.number}</a>.</>
            )}
          </span>
        </Alert>
      )}

      <div style={{ display: 'flex', gap: 24, borderBottom: `1px solid ${c.border}` }}>
        {tabs.map(label => {
          const isActive = label === tab;
          const showLiveDot = label === 'Live Session' && liveSessionState !== 'idle';
          const liveDotColor =
            liveSessionState === 'connected'
              ? '#22c55e'
              : liveSessionState === 'connecting'
                ? '#eab308'
                : '#ef4444';
          return (
            <button
              key={label}
              type="button"
              onClick={() => {
                setTab(label);
                onTabChange(label);
              }}
              style={{
                padding: '0 0 12px',
                fontFamily: 'inherit',
                fontSize: 14,
                fontWeight: 475,
                letterSpacing: '0.28px',
                color: isActive ? c.text : c.textMuted,
                borderBottom: `2px solid ${isActive ? c.brand : 'transparent'}`,
                borderTop: 0,
                borderLeft: 0,
                borderRight: 0,
                background: 'none',
                cursor: 'pointer',
              }}
            >
              {showLiveDot && (
                <span
                  aria-label={`Live session ${liveSessionState}`}
                  title={`Live session ${liveSessionState}`}
                  style={{
                    display: 'inline-block',
                    width: 8,
                    height: 8,
                    borderRadius: 999,
                    marginRight: 7,
                    background: liveDotColor,
                    boxShadow: `0 0 0 2px ${c.surface}, 0 0 10px ${liveDotColor}`,
                    verticalAlign: '1px',
                  }}
                />
              )}
              {label}
            </button>
          );
        })}
      </div>

      <div style={{ display: tab === 'Live Session' ? 'flex' : 'none', flexDirection: 'column', flex: 1 }}>
        {!app?.archived && (
          <LiveSessionTerminal
            repo={repo}
            active={visible && tab === 'Live Session'}
            onStateChange={state => {
              setLiveSessionState(state);
              onLiveSessionStateChange(state);
            }}
          />
        )}
      </div>

      {tab === 'Overview' ? (
        <div className="app-overview-grid">
          <div className="app-about-card"
            style={{
              ...cardStyle,
              boxShadow: 'none',
              padding: 24,
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
            }}
          >
            <span style={{ fontSize: 18, fontWeight: 475, letterSpacing: '0.36px' }}>About</span>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(100px, 0.8fr) minmax(0, 1.2fr)',
                gap: '12px 20px',
                fontSize: 14,
              }}
            >
              <span style={{ color: c.textMuted }}>Owner</span>
              <span>{app?.primaryOwner ?? '—'}</span>
              <span style={{ color: c.textMuted }}>Created by</span>
              <span>{app?.creator ?? '—'}</span>
              <span style={{ color: c.textMuted }}>Repository</span>
              {app ? (
                <a href={app.htmlUrl} target="_blank" rel="noreferrer">
                  Abridge-Commercial/{app.repo}
                </a>
              ) : (
                <span>—</span>
              )}
              <span style={{ color: c.textMuted }}>Visibility</span>
              <span>{app ? (app.visibility === 'public' ? 'Public' : 'Private') : '—'}</span>
              <span style={{ color: c.textMuted }}>Last deploy</span>
              <span>{relativeTime(app?.pushedAt ?? null)}</span>
              <span style={{ color: c.textMuted }}>Status</span>
              <div>{app && <AppStatusTag app={app} visible={visible} />}</div>
            </div>
          </div>

          <div
            className="app-urls-card"
            style={{
              ...cardStyle,
              boxShadow: 'none',
              padding: 24,
              display: 'flex',
              flexDirection: 'column',
              gap: 18,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <span style={{ fontSize: 18, fontWeight: 475, letterSpacing: '0.36px' }}>App URLs</span>
              <span style={{ color: c.textMuted, fontSize: 13.5, lineHeight: 1.45 }}>
                Open or copy the deployed {devOnly ? 'Dev application' : 'Dev and Prod applications'}.
              </span>
            </div>

            <div className="app-url-list">
              <div className="app-url-row">
                <span style={{ color: c.textMuted }}>Dev</span>
                <div className="app-url-value">
                  {!app ? (
                    <span style={{ color: c.textMuted }}>Checking...</span>
                  ) : readinessUnavailable ? (
                    <span style={{ color: c.textMuted }}>Unavailable</span>
                  ) : cloudRunUrl ? (
                    <>
                      <a href={cloudRunUrl} target="_blank" rel="noreferrer" className="app-url-link">
                        {cloudRunUrl}
                      </a>
                      <button type="button" className="app-url-copy" aria-label={cloudRunUrlCopied === 'dev' ? 'Dev URL copied' : 'Copy Dev URL'} title={cloudRunUrlCopied === 'dev' ? 'Copied!' : 'Copy Dev URL'} onClick={() => copyAppUrl(cloudRunUrl, 'dev')}>
                        {cloudRunUrlCopied === 'dev' ? <Icon name={icons.available} size="sm" /> : <VibesIcon name="link" size={16} />}
                      </button>
                    </>
                  ) : (
                    <span style={{ color: c.textMuted }}>Not deployed yet</span>
                  )}
                </div>
              </div>

              {!devOnly && !proposedDevOnly && <div className="app-url-row">
                <span style={{ color: c.textMuted }}>Prod</span>
                <div className="app-url-value">
                  {!app ? (
                    <span style={{ color: c.textMuted }}>Checking...</span>
                  ) : readinessUnavailable ? (
                    <span style={{ color: c.textMuted }}>Unavailable</span>
                  ) : displayedProdUrl ? (
                    <>
                      <a
                        href={displayedProdUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="app-url-link"
                      >
                        {displayedProdUrl}
                      </a>
                      <button
                        type="button"
                        className="app-url-copy"
                        aria-label={cloudRunUrlCopied === 'prod' ? 'Prod URL copied' : 'Copy Prod URL'}
                        title={cloudRunUrlCopied === 'prod' ? 'Copied!' : 'Copy Prod URL'}
                        onClick={() => copyAppUrl(displayedProdUrl, 'prod')}
                      >
                        {cloudRunUrlCopied === 'prod' ? <Icon name={icons.available} size="sm" /> : <VibesIcon name="link" size={16} />}
                      </button>
                      {customProdUrl && prodCloudRunUrl && (
                        <details className="app-url-disclosure">
                          <summary>
                            <Icon
                              name={icons.chevronRight}
                              size="sm"
                              color={c.brandLink}
                              className="app-url-disclosure-chevron"
                            />
                            <span>Show original Cloud Run URL</span>
                          </summary>
                          <div className="app-url-original">
                            <a href={prodCloudRunUrl} target="_blank" rel="noreferrer" className="app-url-link">
                              {prodCloudRunUrl}
                            </a>
                            <button
                              type="button"
                              className="app-url-copy"
                              aria-label={cloudRunUrlCopied === 'prod-original' ? 'original Prod URL copied' : 'Copy original Prod URL'}
                        title={cloudRunUrlCopied === 'prod-original' ? 'Copied!' : 'Copy original Prod URL'}
                        onClick={() => copyAppUrl(prodCloudRunUrl, 'prod-original')}
                            >
                              {cloudRunUrlCopied === 'prod-original' ? <Icon name={icons.available} size="sm" /> : <VibesIcon name="link" size={16} />}
                            </button>
                          </div>
                        </details>
                      )}
                    </>
                  ) : (
                    <span style={{ color: c.textMuted }}>Not deployed yet</span>
                  )}

                </div>
              </div>}
              {previewOnly && <div className="app-url-vanity-action">
                  <button type="button" className="app-url-custom-link" onClick={() => {
                    setCustomUrlEnvironment(devOnly || proposedDevOnly ? 'dev' : 'prod');
                    setCustomUrlPrefix(devOnly || proposedDevOnly ? '' : customUrlPrefixFrom(customProdUrl));
                    setCustomUrlAvailability('idle');
                    setCustomUrlModalOpen(true);
                  }}>Create/edit a vanity URL</button>
                  <span style={{ fontSize: 12, color: c.textMuted }}>Pending feature · Try a mock address for your {devOnly || proposedDevOnly ? 'Dev app' : 'Dev or Prod app'}.</span>
              </div>}
            </div>
          </div>
          {app && !app.archived && !capabilitiesLoading && !devOnly && !proposedDevOnly && (
            <DeploymentsPanel active={visible} repo={repo} canManage={canManage} prodReady={Boolean(app.readiness.prodUrl)}
              previewOnly={previewOnly} view="overview" onViewDeployments={() => { setTab('Deployments'); onTabChange('Deployments'); }} />
          )}
        </div>
      ) : tab === 'Deployments' ? (
        <DeploymentsPanel active={visible} previewOnly={previewOnly} repo={repo} canManage={canManage} devOnly={devOnly || proposedDevOnly} prodReady={Boolean(app?.readiness.prodUrl)} />
      ) : tab === 'Secrets' ? (
        pendingCapabilities.secretsManagement && !enabledCapabilities.secretsManagement ? (
          <Alert variant="info" showIcon>
            Secrets Management is pending platform review. Secret values can be added after the
            infrastructure request is approved and provisioned.
          </Alert>
        ) : (
           <SecretsPanel repo={repo} canManage={canManage} devOnly={devOnly || proposedDevOnly} />
        )
      ) : tab === 'Docs' ? (
        <ReadmePanel repo={repo} />
      ) : tab === 'Live Session' ? null : tab === 'Settings' ? (
        <div
          style={{
            ...cardStyle,
            padding: 24,
            display: 'flex',
            flexDirection: 'column',
            gap: 18,
            maxWidth: 720,
          }}
        >
          {canManage && <section className="app-settings-section" aria-labelledby="capabilities-section-title">
            <header className="app-settings-section-heading">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <h2 id="capabilities-section-title">Application capabilities</h2>
                {hasUnsavedCapabilities && <Tag variant="warning" size="sm">Unsaved changes</Tag>}
              </div>
              <p>Choose the services your app needs.</p>
            </header>
          {canManage && (
            <div
              style={{
                border: `1px solid ${c.border}`,
                borderRadius: 14,
                padding: 18,
                display: 'flex',
                flexDirection: 'column',
                gap: 14,
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={{ fontSize: 13.5, lineHeight: 1.45, color: c.textBody }}>
                  Add capabilities to your app upon admin approval. <a href={vibeCheckHref} style={{ color: c.brandLink, textDecoration: 'underline', textUnderlineOffset: 2 }}>Vibe Check</a> or consult the Vibes MCP!
                </span>
              </div>

              {capabilitiesError && (
                <Alert variant="danger" showIcon>{capabilitiesError}</Alert>
              )}
              {capabilityChangePending && (
                <Alert variant="info" showIcon>
                  Capability request pending platform review.
                </Alert>
              )}

              {!capabilitiesLoading && (
                <AppServicePicker
                  helpPresentation="guide"
                  value={capabilityDraft}
                  onChange={next => setCapabilityDraft(current => ({ ...current, ...next }))}
                  secretsManagement={capabilityDraft.secretsManagement}
                  onSecretsManagementChange={secretsManagement =>
                    setCapabilityDraft(current => ({ ...current, secretsManagement }))
                  }
                  enabled={enabledCapabilities}
                  pending={pendingCapabilities}
                  schedulerCron={schedulerCron}
                  schedulerTimeZone={schedulerTimeZone}
                  onSchedulerCronChange={setSchedulerCron}
                  cloudSqlReadiness={cloudSqlReadiness}
                  cloudSqlReadinessError={cloudSqlReadinessError}
                  cloudSqlRuntimeBusy={cloudSqlRuntimeBusy}
                  cloudSqlRuntimeResults={cloudSqlRuntimeResults}
                  cloudSqlRuntimeErrors={cloudSqlRuntimeErrors}
                  onReconcileCloudSqlRuntime={reconcileCloudSqlRuntime}
                />
              )}

              <div className={hasUnsavedCapabilities ? 'capability-request-actions unsaved' : 'capability-request-actions'}>
                {hasUnsavedCapabilities && <div role="status" className="capability-unsaved-notice">
                  <strong>Changes aren’t saved yet.</strong>
                  <span>Select “Request capability changes” to submit them for platform review.</span>
                </div>}
                <Button
                className="app-settings-action"
                  variant={hasUnsavedCapabilities ? 'primary' : 'secondary'}
                  size="sm"
                  disabled={
                    capabilitiesLoading ||
                    capabilitiesSaving ||
                    (capabilityDraft.cloudScheduler && Boolean(schedulerCronError(schedulerCron))) ||
                    !hasUnsavedCapabilities
                  }
                  onPress={() => void saveCapabilityEnablement()}
                >
                  {capabilitiesSaving ? 'Opening request…' : 'Request capability changes'}
                </Button>
              </div>
            </div>
          )}
          </section>}

          <section className="app-settings-section" aria-labelledby="access-section-title">
            <header className="app-settings-section-heading">
              <h2 id="access-section-title">Access &amp; visibility</h2>
              <p>Choose who can find, build, and use this app.</p>
              <AccessExplainer />
            </header>

          <div
            style={{
              border: `1px solid ${c.border}`,
              borderRadius: 14,
              padding: 18,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 18,
              background: c.surface,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
              <span style={{ fontSize: 15, fontWeight: 475 }}>Catalog visibility</span>
              <span style={{ fontSize: 13.5, lineHeight: 1.45, color: c.textBody }}>
                Public apps are available to all Abridge users in the catalog and at the app URL. Only contributors can edit the app or manage its settings.
              </span>
              {!canManage && <Tag variant="default">{app?.visibility === 'public' ? 'Public' : 'Private'}</Tag>}
              {visibilityError && (
                <Alert variant="danger" showIcon>
                  <span style={{ fontSize: 14, lineHeight: 1.35 }}>{visibilityError}</span>
                </Alert>
              )}
            </div>
            {canManage && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <div className="environment-selector" role="group" aria-label="Catalog visibility">
                  {(['private', 'public'] as const).map(visibility => (
                    <button key={visibility} type="button" aria-pressed={visibilityDraft === visibility}
                      disabled={!app || visibilitySaving}
                      onClick={() => setVisibilityDraft(visibility)}>
                      {visibilityDraft === visibility && <span aria-hidden="true">✓ </span>}
                      {visibility === 'public' ? 'Public' : 'Private'}
                    </button>
                  ))}
                </div>
                {app && visibilityDraft !== app.visibility && <Button
                className="app-settings-action"
                  variant="primary"
                  size="sm"
                  loading={visibilitySaving}
                  disabled={visibilitySaving}
                  style={{ flex: '0 0 auto', minWidth: 72, whiteSpace: 'nowrap' }}
                  onPress={saveCatalogVisibility}
                >Save</Button>}
              </div>
            )}
          </div>

          <div style={{ height: 1, background: c.border }} />

          <div
            style={{
              border: `1px solid ${c.border}`,
              borderRadius: 14,
              padding: 18,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 18,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, minWidth: 0 }}>
              <span style={{ fontSize: 15, fontWeight: 475 }}>Collaborators</span>
              <span style={{ fontSize: 13.5, lineHeight: 1.45, color: c.textBody }}>
                Who can develop this app and manage its settings.
                <br />
                <strong>Requires a GitHub account and access to Vibes.</strong>
              </span>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {app?.owner && app.owner !== 'cdp-admins' && (
                  <Tag variant="default">team: {app.owner}</Tag>
                )}
                {(app?.ownerUsers ?? []).filter(user => user !== app?.primaryOwner).map(user => (
                  <Tag key={user} variant="default">user: {user}</Tag>
                ))}
                {(app?.ownerGroups ?? []).filter(group => group !== app?.owner && group !== 'cdp-admins').map(group => (
                  <Tag key={group} variant="default">group: {group}</Tag>
                ))}
              </div>
            </div>
            {canManage && (
              <Button
                className="app-settings-action"
                variant="secondary"
                size="sm"
                disabled={!app}
                style={{ flex: '0 0 auto', minWidth: 104, whiteSpace: 'nowrap' }}
                onPress={openOwnersModal}
              >
                Manage collaborators
              </Button>
            )}
          </div>

          <div style={{ height: 1, background: c.border }} />

          <div
            style={{
              border: `1px solid ${c.border}`,
              borderRadius: 14,
              padding: 18,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 18,
              background: appAccessUnavailable ? c.subtle : c.surface,
              opacity: appAccessUnavailable ? 0.62 : 1,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span id="application-access-heading" style={{ fontSize: 15, fontWeight: 475 }}>App access</span>
              <span style={{ fontSize: 13.5, lineHeight: 1.45, color: c.textBody }}>
                {isPublic
                  ? iapAccessUnavailable
                    ? 'This app is public and available to all Abridge users. IAP is disabled, so sign-in and any additional restrictions are managed by the app’s approved authentication provider.'
                    : 'This app is public and available to all Abridge users.'
                  : iapAccessUnavailable
                  ? 'Application Access is unavailable because IAP is disabled. Access is managed by the app’s approved authentication provider.'
                  : 'If your app has a web interface, these users can sign in and use it.'}
              </span>
              {!appAccessUnavailable && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {applicationAccess.allAbridgeUsers.dev && applicationAccess.allAbridgeUsers.prod ? (
                  <Tag variant="default">All Abridge users</Tag>
                ) : (
                  <>
                    <Tag variant="default">
                      Dev: {applicationAccess.allAbridgeUsers.dev ? 'All Abridge users' : `${applicationAccess.dev.length} ${isPublic ? 'explicit users' : 'users'}`}
                    </Tag>
                    <Tag variant="default">
                      Prod: {applicationAccess.allAbridgeUsers.prod ? 'All Abridge users' : `${applicationAccess.prod.length} ${isPublic ? 'explicit users' : 'users'}`}
                    </Tag>
                  </>
                )}
                {(applicationAccess.dev.join(',') !== applicationAccess.prod.join(',')
                  || applicationAccess.allAbridgeUsers.dev !== applicationAccess.allAbridgeUsers.prod) && (
                  <Tag variant="warning">Lists differ</Tag>
                )}
              </div>}
            </div>
            {canManage && (
              <Button
                className="app-settings-action"
                variant="secondary"
                size="sm"
                disabled={!app || appAccessUnavailable}
                style={{ flex: '0 0 auto', minWidth: 104, whiteSpace: 'nowrap' }}
                onPress={openAccessModal}
              >
                Manage app access
              </Button>
            )}
          </div>
          {canManage && (
            <div
              style={{
                border: `1px solid ${c.border}`,
                borderRadius: 14,
                padding: 18,
                display: 'flex',
                flexDirection: 'column',
                gap: 14,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 7, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 15, fontWeight: 475 }}>Require IAP authentication</span>
                    {iapChangePending ? (
                      <Tag variant="warning">{proposedIapEnabled ? 'Enable requested' : 'Disable requested'}</Tag>
                    ) : iapDeploymentPending ? (
                      <Tag variant="warning">Applying</Tag>
                    ) : iapEnabled ? (
                      <Tag variant="success">Enabled</Tag>
                    ) : (
                      <Tag variant="default">Disabled</Tag>
                    )}
                  </div>
                  <span style={{ fontSize: 13.5, lineHeight: 1.45, color: c.textBody }}>
                    Enabled by default. Do not disable IAP unless your app uses another approved
                    authentication method. Changes apply to {devOnly ? 'Dev' : 'Dev and Prod'}.
                  </span>
                  <button
                    type="button"
                    aria-expanded={iapHelpOpen}
                    aria-controls="iap-help-content"
                    onClick={() => setIapHelpOpen(open => !open)}
                    style={{
                      alignSelf: 'flex-start',
                      padding: 0,
                      border: 0,
                      background: 'transparent',
                      color: c.brandLink,
                      font: 'inherit',
                      fontSize: 13,
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      textUnderlineOffset: 2,
                    }}
                  >
                    What is IAP?
                  </button>
                  {iapHelpOpen && (
                    <p id="iap-help-content" style={{ margin: 0, maxWidth: '62ch', fontSize: 13, lineHeight: 1.5, color: c.textMuted }}>
                      Identity-Aware Proxy (IAP) authenticates users before requests reach your
                      application, so you don&apos;t need to build authentication yourself. Keep it
                      enabled unless your app uses another approved provider, such as Okta.
                      Disabling IAP requires justification and Vibes admin approval.
                    </p>
                  )}
                </div>
                <Switch
                  checked={iapEnabled}
                  disabled={capabilitiesLoading || iapSaving || iapChangePending || iapDeploymentPending}
                  onCheckedChange={enabled => {
                    setIapError(null);
                    if (enabled) void requestIapChange(true);
                    else {
                      setIapJustificationDraft('');
                      setIapRequestModalOpen(true);
                    }
                  }}
                  aria-label={devOnly ? 'Require IAP authentication for Dev' : 'Require IAP authentication for Dev and Prod'}
                />
              </div>

              {iapError && <Alert variant="danger" showIcon>{iapError}</Alert>}
              {iapChangePending && (
                <Alert variant="info" showIcon>
                  The IAP change is pending platform review.
                </Alert>
              )}
              {iapDeploymentPending && (
                <Alert variant="info" showIcon>
                  The approved IAP setting is being applied to Dev and Prod.
                </Alert>
              )}
              {!iapEnabled && !iapChangePending && iapExceptionJustification && (
                <span style={{ fontSize: 12.5, lineHeight: 1.45, color: c.textMuted }}>
                  Approved justification: {iapExceptionJustification}
                </span>
              )}
            </div>
          )}
          </section>

          {(canTransferOwnership || (canManage && !app?.archived) || isArchivedAdmin) && <section className="app-settings-section app-settings-danger" aria-labelledby="danger-section-title">
            <header className="app-settings-section-heading">
              <h2 id="danger-section-title">Danger zone</h2>
              <p>Changes to ownership, environments, and app lifecycle. Review carefully before continuing.</p>
            </header>
          {canTransferOwnership && <div
            style={{
              border: `1px solid ${c.brand}`,
              borderRadius: 14,
              padding: 18,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 18,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
              <span style={{ fontSize: 15, fontWeight: 475 }}>Transfer ownership</span>
              <span style={{ fontSize: 13.5, lineHeight: 1.45, color: c.textBody }}>
                Make another Abridge GitHub member the primary owner of this app.
              </span>
            </div>
            <Button
                className="app-settings-action"
              variant="danger"
              size="sm"
              disabled={!app}
              style={{ flex: '0 0 auto', minWidth: 136, whiteSpace: 'nowrap' }}
              onPress={openTransferModal}
            >
              Transfer ownership
            </Button>
          </div>}

          {canTransferOwnership && <div style={{ height: 1, background: c.border }} />}

          {isCdpAdmin && canManage && !app?.archived && <>
            <div
              style={{
                border: `1px solid ${c.brand}`,
                borderRadius: 14,
                padding: 18,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 18,
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: '1 1 300px' }}>
                <span style={{ fontSize: 15, fontWeight: 475 }}>Application environments</span>
                <span style={{ fontSize: 13.5, lineHeight: 1.45, color: c.textBody }}>
                  {devOnly
                    ? 'Dev only. Restoring Prod recreates its infrastructure, not previously deleted data.'
                    : 'Dev and Prod. Switching to Dev only deletes all Prod infrastructure, identities, secrets, and data.'}
                  {' '}Changes require an admin reason and a reviewed infrastructure PR.
                </span>
                {environmentChangePending && (
                  <Alert variant="warning" showIcon>
                    {proposedDevOnly ? 'Removing Prod' : 'Restoring Prod'} is pending platform review.
                    {capabilitiesPullRequestUrl && <a href={capabilitiesPullRequestUrl} target="_blank" rel="noreferrer"> View PR</a>}
                  </Alert>
                )}
                {environmentError && !environmentModalOpen && <Alert variant="danger" showIcon>{environmentError}</Alert>}
              </div>
              <div className="environment-selector" role="group" aria-label="Application environments">
                {[{ label: 'Dev only', value: true }, { label: 'Dev + Prod', value: false }].map(option => (
                  <button key={option.label} type="button" aria-pressed={devOnly === option.value}
                    disabled={!app || capabilitiesLoading || environmentSaving || environmentChangePending}
                    onClick={() => {
                      if (option.value === devOnly) return;
                      setEnvironmentTargetDevOnly(option.value);
                      setEnvironmentReason('');
                      setEnvironmentConfirmText('');
                      setEnvironmentError(null);
                      setEnvironmentModalOpen(true);
                    }}>
                    {devOnly === option.value && <span aria-hidden="true">✓ </span>}{option.label}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ height: 1, background: c.border }} />
          </>}

          {canManage && !app?.archived && <div
            style={{
              border: `1px solid ${c.brand}`,
              borderRadius: 14,
              padding: 18,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 18,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 15, fontWeight: 475 }}>Archive app</span>
              <span style={{ fontSize: 13.5, lineHeight: 1.45, color: c.textBody }}>
                Makes the repository read-only, removes the app from the ordinary catalog, and opens
                a reviewed request to decommission all managed infrastructure, data, secrets, images,
                inventory, and backend state.
              </span>
            </div>
            <Button
                className="app-settings-action"
              variant="danger"
              size="sm"
              loading={archiving && archiveModalOpen}
              disabled={!app || archiving}
              style={{ flex: '0 0 auto', minWidth: 104, whiteSpace: 'nowrap' }}
              onPress={() => {
                setArchiveConfirmText('');
                setArchiveError(null);
                setArchiveModalOpen(true);
              }}
            >
              Archive app
            </Button>
          </div>}

          {isArchivedAdmin && (
            <>
              <div
                style={{
                  border: `1px solid ${c.border}`,
                  borderRadius: 14,
                  padding: 18,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 18,
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
                  <span style={{ fontSize: 15, fontWeight: 475 }}>Restore app</span>
                  <span style={{ fontSize: 13.5, lineHeight: 1.45, color: c.textBody }}>
                    If teardown is pending, cancel it and reactivate the repository. If teardown was
                    approved, open a reviewed pull request to redeploy the last trusted infrastructure configuration.
                  </span>
                </div>
                <Button variant="secondary" size="sm" onPress={() => {
                  setArchivedActionError(null);
                  setArchivedAction('restore');
                }}>
                  Restore app
                </Button>
              </div>

              <div style={{ height: 1, background: c.border }} />

              <div
                style={{
                  border: `1px solid ${c.brand}`,
                  borderRadius: 14,
                  padding: 18,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 18,
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
                  <span style={{ fontSize: 15, fontWeight: 475 }}>Permanently delete app</span>
                  <span style={{ fontSize: 13.5, lineHeight: 1.45, color: c.textBody }}>
                    Queue permanent repository deletion. The protected workflow finishes all
                    infrastructure cleanup before deleting the GitHub repository.
                  </span>
                </div>
                <Button variant="danger" size="sm" onPress={() => {
                  setDeleteConfirmText('');
                  setArchivedActionError(null);
                  setArchivedAction('delete');
                }}>
                  Permanently delete
                </Button>
              </div>

              {deletionPullRequestUrl && (
                <Alert variant="warning" showIcon>
                  Permanent deletion is queued for platform review.{' '}
                  <a href={deletionPullRequestUrl} target="_blank" rel="noreferrer">View request</a>.
                </Alert>
              )}
              {archivedActionError && <Alert variant="danger" showIcon>{archivedActionError}</Alert>}
            </>
          )}

          {archiveError && (
            <Alert variant="danger" showIcon>
              <span style={{ fontSize: 14, lineHeight: 1.35 }}>{archiveError}</span>
            </Alert>
          )}
          </section>}

        </div>
      ) : null}

      {transferStep && app && (
        <div
          role="presentation"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 50,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            background: 'rgba(21,20,19,0.48)',
          }}
          onMouseDown={event => {
            if (event.target === event.currentTarget && !transferSaving) setTransferStep(null);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="transfer-ownership-title"
            style={{
              ...cardStyle,
              width: 'min(100%, 560px)',
              boxSizing: 'border-box',
              maxHeight: 'calc(100vh - 48px)',
              overflowY: 'auto',
              padding: 24,
              display: 'flex',
              flexDirection: 'column',
              gap: 18,
            }}
          >
            {transferStep === 1 && (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <span id="transfer-ownership-title" style={{ fontSize: 20, fontWeight: 475, letterSpacing: '0.4px' }}>
                    Choose the new owner
                  </span>
                  <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5, color: c.textBody }}>
                    Search for an Abridge-Commercial GitHub member. The selected person will become
                    the primary owner of {app.repo}.
                  </p>
                </div>

                <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 475 }}>New owner</span>
                  <Input
                    value={transferNewOwner}
                    onChange={event => {
                      setTransferNewOwner(event.target.value);
                      setTransferOwnerValidated(false);
                      setTransferError(null);
                    }}
                    placeholder="Search GitHub members"
                    disabled={transferSaving}
                    aria-label="New primary owner"
                    autoFocus
                  />
                </label>

                {transferSuggestionsLoading && <span style={{ fontSize: 12.5, color: c.textMuted }}>Searching GitHub…</span>}
                {transferSuggestionsError && (
                  <Alert variant="danger" showIcon>
                    <span style={{ fontSize: 14, lineHeight: 1.35 }}>{transferSuggestionsError}</span>
                  </Alert>
                )}
                {!transferSuggestionsLoading && !transferSuggestionsError && transferNewOwner.trim().length >= 2 && !transferOwnerValidated && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {transferSuggestions.length ? transferSuggestions.map(login => (
                      <button
                        key={login}
                        type="button"
                        onClick={() => {
                          setTransferNewOwner(login.toLowerCase());
                          setTransferOwnerValidated(true);
                          setTransferSuggestions([]);
                        }}
                        style={{
                          padding: '10px 12px',
                          border: `1px solid ${c.border}`,
                          borderRadius: 10,
                          background: c.surface,
                          color: c.text,
                          fontFamily: 'inherit',
                          fontSize: 13.5,
                          textAlign: 'left',
                          cursor: 'pointer',
                        }}
                      >
                        {login}
                      </button>
                    )) : <span style={{ fontSize: 12.5, color: c.textMuted }}>No matching organization members.</span>}
                  </div>
                )}
                {transferOwnerValidated && (
                  <Alert variant="info" showIcon>
                    <span style={{ fontSize: 14, lineHeight: 1.35 }}>
                      <strong>{transferNewOwner}</strong> is an Abridge-Commercial member.
                    </span>
                  </Alert>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                  <Button variant="outlined" size="sm" onPress={() => setTransferStep(null)}>Cancel</Button>
                  <Button variant="primary" size="sm" disabled={!transferOwnerValidated} onPress={continueTransfer}>
                    Continue
                  </Button>
                </div>
              </>
            )}

            {transferStep === 2 && (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <span id="transfer-ownership-title" style={{ fontSize: 20, fontWeight: 475, letterSpacing: '0.4px' }}>
                    {isCurrentPrimaryOwner ? 'Do you want to keep contributing?' : `Keep ${app.primaryOwner} as an additional owner?`}
                  </span>
                  <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5, color: c.textBody }}>
                    {isCurrentPrimaryOwner
                      ? 'You are not currently listed as an additional owner. Choose whether your GitHub account should keep individual repository and portal management access after the transfer.'
                      : `Choose whether ${app.primaryOwner} should keep individual repository and portal management access after the transfer.`}
                  </p>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    size="sm"
                    onPress={() => {
                      setKeepCurrentOwner(false);
                      setTransferStep(3);
                    }}
                  >
                    {isCurrentPrimaryOwner ? 'No, keep me out' : 'No, remove access'}
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onPress={() => {
                      setKeepCurrentOwner(true);
                      setTransferStep(3);
                    }}
                  >
                    {isCurrentPrimaryOwner ? 'Yes, add me' : 'Yes, keep access'}
                  </Button>
                </div>
              </>
            )}

            {transferStep === 3 && (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <span id="transfer-ownership-title" style={{ fontSize: 20, fontWeight: 475, letterSpacing: '0.4px' }}>
                    Confirm ownership transfer
                  </span>
                  <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5, color: c.textBody }}>
                    You are transferring primary ownership of <strong>{app.repo}</strong> from{' '}
                    <strong>{app.primaryOwner}</strong> to <strong>{transferNewOwner}</strong>.
                  </p>
                </div>

                <Alert variant="warning" showIcon>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 14, lineHeight: 1.4 }}>
                    <span>The new owner will receive primary portal management and GitHub maintainer access.</span>
                    <span>
                      {keepCurrentOwner
                        ? `${isCurrentPrimaryOwner ? 'You' : app.primaryOwner} will remain an additional owner with management and maintainer access.`
                        : `${isCurrentPrimaryOwner ? 'Your' : `${app.primaryOwner}'s`} individual owner and repository collaborator access will be removed. Team-based access may still apply.`}
                    </span>
                    <span>The new owner or a CDP admin can transfer ownership again. The original creator record will not change.</span>
                  </div>
                </Alert>

                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13.5, lineHeight: 1.4, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={transferAccepted}
                    onChange={event => setTransferAccepted(event.target.checked)}
                    disabled={transferSaving}
                    style={{ marginTop: 2 }}
                  />
                  <span>I understand these changes and want to transfer ownership.</span>
                </label>

                {transferError && (
                  <Alert variant="danger" showIcon>
                    <span style={{ fontSize: 14, lineHeight: 1.35 }}>{transferError}</span>
                  </Alert>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                  <Button variant="outlined" size="sm" disabled={transferSaving} onPress={() => setTransferStep(null)}>
                    Cancel
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    loading={transferSaving}
                    disabled={!transferAccepted || transferSaving}
                    onPress={transferOwnership}
                  >
                    Transfer ownership
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {previewOnly && customUrlModalOpen && (
        <div
          role="presentation"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 50,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            background: 'rgba(21,20,19,0.48)',
          }}
          onMouseDown={event => {
            if (event.target === event.currentTarget) setCustomUrlModalOpen(false);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="vanity-url-title"
            style={{
              ...cardStyle,
              width: 'min(100%, 520px)',
              padding: 24,
              display: 'flex',
              flexDirection: 'column',
              gap: 20,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span
                  id="vanity-url-title"
                  style={{ fontSize: 20, fontWeight: 475, letterSpacing: '0.4px' }}
                >
                  Create or edit a vanity URL
                </span>
                <Tag variant="default" size="sm">Preview</Tag>
              </div>
              <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5, color: c.textBody }}>
                {customUrlEnvironment === 'prod' && customProdUrl
                  ? 'Update the prefix for your current vanity address.'
                  : 'Choose the prefix for a new vanity address.'}{' '}
                Availability checking is simulated while this feature is pending.
              </p>
            </div>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13.5 }}>
              Points to environment
              <select className="vanity-environment" value={customUrlEnvironment} onChange={event => {
                const next = event.target.value as 'dev' | 'prod';
                setCustomUrlEnvironment(next);
                setCustomUrlPrefix(next === 'prod' ? customUrlPrefixFrom(customProdUrl) : '');
              }}>
                <option value="dev">Dev</option>
                {!devOnly && !proposedDevOnly && <option value="prod">Prod</option>}
              </select>
            </label>
            <FormField
              label="URL prefix"
              controlId="vanity-url-prefix"
              helperText="Use lowercase letters, numbers, and hyphens."
              error={customUrlPrefix.length > 0 && !CUSTOM_URL_PREFIX_PATTERN.test(customUrlPrefix.trim())
                ? 'Enter 1–40 characters without leading or trailing hyphens.'
                : undefined}
            >
              <div className="custom-url-input">
                <Input
                  id="vanity-url-prefix"
                  aria-label="URL prefix"
                  value={customUrlPrefix}
                  onChange={event => setCustomUrlPrefix(event.target.value.toLowerCase())}
                  placeholder="my-app"
                  invalid={customUrlPrefix.length > 0 && !CUSTOM_URL_PREFIX_PATTERN.test(customUrlPrefix.trim())}
                  aria-describedby="vanity-url-status"
                  autoFocus
                />
                <span>.corp.abridge.com</span>
              </div>
            </FormField>

            <div id="vanity-url-status" aria-live="polite" style={{ minHeight: 21, fontSize: 13.5 }}>
              {customUrlAvailability === 'checking' && (
                <span style={{ color: c.textMuted }}>Checking availability...</span>
              )}
              {customUrlAvailability === 'available' && (
                <span style={{ color: c.success }}>
                  {customUrlPrefix.trim()}.corp.abridge.com is available (simulated).
                </span>
              )}
              {customUrlAvailability === 'unavailable' && (
                <span style={{ color: c.brandLink }}>
                  {customUrlPrefix.trim()}.corp.abridge.com is already in use (simulated).
                </span>
              )}
            </div>

            <Alert variant="info" showIcon>
              <span style={{ fontSize: 13.5, lineHeight: 1.4 }}>
                Pending feature: vanity URL requests are not available yet. This mock does not reserve an address or change routing.
              </span>
            </Alert>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <Button variant="outlined" size="sm" onPress={() => setCustomUrlModalOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                disabled
              >
                Submit request
              </Button>
            </div>
          </div>
        </div>
      )}

      {environmentModalOpen && app && (
        <div
          role="presentation"
          style={{
            position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center',
            justifyContent: 'center', padding: 24, background: 'rgba(21,20,19,0.48)',
          }}
          onMouseDown={event => {
            if (event.target === event.currentTarget && !environmentSaving) setEnvironmentModalOpen(false);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="environment-mode-title"
            style={{ ...cardStyle, width: 'min(100%, 520px)', padding: 24, display: 'flex', flexDirection: 'column', gap: 18 }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span id="environment-mode-title" style={{ fontSize: 20, fontWeight: 475 }}>
                {environmentTargetDevOnly ? 'Remove Prod' : 'Restore Prod'} for {app.repo}?
              </span>
              <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5, color: c.textBody }}>
                {environmentTargetDevOnly
                  ? 'An approved PR will delete the app’s Prod infrastructure, identities, secrets, and data. Dev remains available.'
                  : 'An approved PR will recreate Prod infrastructure and identities. Deleted Prod data and secrets will not return.'}
                {' '}Your reason, GitHub identity, and the request timestamp will be recorded in Firestore before the PR is created.
              </p>
            </div>
            <label htmlFor="environment-mode-reason" style={{ fontSize: 13.5 }}>Reason for this change</label>
            <textarea
              id="environment-mode-reason"
              value={environmentReason}
              onChange={event => setEnvironmentReason(event.target.value)}
              disabled={environmentSaving}
              maxLength={2000}
              rows={4}
              placeholder="Explain why this app should change environments (at least 20 characters)."
              style={{ width: '100%', boxSizing: 'border-box', border: `1px solid ${c.border}`, borderRadius: 8, padding: 12, font: 'inherit' }}
              autoFocus
            />
            <Alert variant="warning" showIcon>
              To confirm, type <strong>{app.repo}</strong> below.
            </Alert>
            <Input
              value={environmentConfirmText}
              onChange={event => setEnvironmentConfirmText(event.target.value)}
              placeholder={app.repo}
              disabled={environmentSaving}
              aria-label={`Type ${app.repo} to confirm changing environments`}
            />
            {environmentError && <Alert variant="danger" showIcon>{environmentError}</Alert>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <Button variant="outlined" size="sm" disabled={environmentSaving} onPress={() => setEnvironmentModalOpen(false)}>
                Cancel
              </Button>
              <Button
                variant={environmentTargetDevOnly ? 'danger' : 'primary'}
                size="sm"
                loading={environmentSaving}
                disabled={environmentSaving || environmentConfirmText !== app.repo || environmentReason.trim().length < 20 || environmentReason.trim().length > 2000}
                onPress={submitEnvironmentMode}
              >
                Request {environmentTargetDevOnly ? 'Dev only' : 'Prod restoration'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {archiveModalOpen && app && (
        <div
          role="presentation"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 50,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            background: 'rgba(21,20,19,0.48)',
          }}
          onMouseDown={event => {
            if (event.target === event.currentTarget && !archiving) setArchiveModalOpen(false);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="archive-app-title"
            style={{
              ...cardStyle,
              width: 'min(100%, 520px)',
              padding: 24,
              display: 'flex',
              flexDirection: 'column',
              gap: 18,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span
                id="archive-app-title"
                style={{ fontSize: 20, fontWeight: 475, letterSpacing: '0.4px' }}
              >
                Archive {app.repo}?
              </span>
              <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5, color: c.textBody }}>
                The GitHub repository will remain available but become read-only. The app will be
                removed from the ordinary catalog, and all managed infrastructure, data containers,
                secrets, images, inventory, and backend state will be decommissioned through a
                reviewed, approval-gated pull request. A cdp-admins member can optionally restore it.
              </p>
            </div>

            <Alert variant="warning" showIcon>
              <span style={{ fontSize: 14, lineHeight: 1.35 }}>
                To confirm, type <strong>{app.repo}</strong> below.
              </span>
            </Alert>

            <Input
              value={archiveConfirmText}
              onChange={event => setArchiveConfirmText(event.target.value)}
              placeholder={app.repo}
              disabled={archiving}
              aria-label={`Type ${app.repo} to confirm archiving`}
              autoFocus
            />

            {archiveError && (
              <Alert variant="danger" showIcon>
                <span style={{ fontSize: 14, lineHeight: 1.35 }}>{archiveError}</span>
              </Alert>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <Button
                variant="outlined"
                size="sm"
                disabled={archiving}
                onPress={() => setArchiveModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                size="sm"
                loading={archiving}
                disabled={!archiveConfirmed || archiving}
                onPress={archiveApp}
              >
                Archive app
              </Button>
            </div>
          </div>
        </div>
      )}

      {archivedAction && app && (
        <div
          role="presentation"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 50,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            background: 'rgba(21,20,19,0.48)',
          }}
          onMouseDown={event => {
            if (event.target === event.currentTarget && !archivedActionBusy) setArchivedAction(null);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="archived-action-title"
            style={{ ...cardStyle, width: 'min(100%, 520px)', padding: 24, display: 'flex', flexDirection: 'column', gap: 18 }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span id="archived-action-title" style={{ fontSize: 20, fontWeight: 475, letterSpacing: '0.4px' }}>
                {archivedAction === 'restore' ? `Restore ${app.repo}?` : `Permanently delete ${app.repo}?`}
              </span>
              <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5, color: c.textBody }}>
                {archivedAction === 'restore'
                  ? app.decommissionPullRequest?.state === 'open'
                    ? 'The pending teardown request will be cancelled and the repository will become active again.'
                    : 'A reviewed infrastructure pull request will restore the last trusted app configuration and redeploy its resources.'
                  : 'Deletion will be recorded in the reviewed teardown request. The repository is permanently deleted only after infrastructure cleanup succeeds.'}
              </p>
            </div>

            {archivedAction === 'delete' && (
              <>
                <Alert variant="warning" showIcon>
                  To confirm permanent deletion, type <strong>{app.repo}</strong> below.
                </Alert>
                <Input
                  value={deleteConfirmText}
                  onChange={event => setDeleteConfirmText(event.target.value)}
                  placeholder={app.repo}
                  disabled={archivedActionBusy}
                  aria-label={`Type ${app.repo} to confirm permanent deletion`}
                  autoFocus
                />
              </>
            )}
            {archivedActionError && <Alert variant="danger" showIcon>{archivedActionError}</Alert>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <Button variant="outlined" size="sm" disabled={archivedActionBusy} onPress={() => setArchivedAction(null)}>
                Cancel
              </Button>
              <Button
                variant={archivedAction === 'delete' ? 'danger' : 'primary'}
                size="sm"
                loading={archivedActionBusy}
                disabled={archivedActionBusy || (archivedAction === 'delete' && !deleteConfirmed)}
                onPress={archivedAction === 'restore' ? restoreApp : requestPermanentDeletion}
              >
                {archivedAction === 'restore' ? 'Restore app' : 'Queue permanent deletion'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {detailsModalOpen && app && (
        <div
          role="presentation"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 50,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            background: 'rgba(21,20,19,0.48)',
          }}
          onMouseDown={event => {
            if (event.target === event.currentTarget && !detailsSaving) setDetailsModalOpen(false);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-app-details-title"
            style={{
              ...cardStyle,
              width: 'min(100%, 560px)',
              padding: 24,
              display: 'flex',
              flexDirection: 'column',
              gap: 18,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span
                id="edit-app-details-title"
                style={{ fontSize: 20, fontWeight: 475, letterSpacing: '0.4px' }}
              >
                Edit app details
              </span>
              <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5, color: c.textBody }}>
                Updates the name, description, and category stored in the portal catalog. Repository files
                are not changed.
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <FormField label="Category" controlId="app-details-type">
                <select id="app-details-type" value={detailsForm.appType ?? ''} disabled={detailsSaving}
                  onChange={event => setDetailsForm(current => ({ ...current, appType: event.target.value || null }))}
                  style={{ width: '100%', font: 'inherit', fontSize: 14, padding: '11px 14px', border: `1px solid ${c.border}`, borderRadius: 10, background: c.surface }}>
                  <option value="">Not set</option>
                  {detailsForm.appType && !APP_TYPES.some(type => type === detailsForm.appType) && <option value={detailsForm.appType}>{detailsForm.appType}</option>}
                  {APP_TYPES.map(type => <option key={type}>{type}</option>)}
                </select>
              </FormField>
              <FormField label="Name" controlId="app-details-title" required>
                <Input
                  id="app-details-title"
                  value={detailsForm.title}
                  disabled={detailsSaving}
                  onChange={event =>
                    setDetailsForm(current => ({ ...current, title: event.target.value }))
                  }
                  placeholder="Visit Summary Exporter"
                  autoFocus
                />
              </FormField>

              <FormField
                label="Description"
                controlId="app-details-description"
                required
                helperText="One line describing the app — shown in the catalog."
              >
                <Input
                  id="app-details-description"
                  value={detailsForm.description}
                  disabled={detailsSaving}
                  onChange={event =>
                    setDetailsForm(current => ({ ...current, description: event.target.value }))
                  }
                  placeholder="Exports nightly visit summaries to the analytics bucket"
                />
              </FormField>
            </div>

            {detailsError && (
              <Alert variant="danger" showIcon>
                <span style={{ fontSize: 14, lineHeight: 1.35 }}>{detailsError}</span>
              </Alert>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <Button
                variant="outlined"
                size="sm"
                disabled={detailsSaving}
                onPress={() => setDetailsModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                loading={detailsSaving}
                disabled={detailsSaving || !detailsForm.title.trim() || !detailsForm.description.trim()}
                onPress={saveDetails}
              >
                Save changes
              </Button>
            </div>
          </div>
        </div>
      )}

      {ownersModalOpen && app && (
        <div
          role="presentation"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 50,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            background: 'rgba(21,20,19,0.48)',
          }}
          onMouseDown={event => {
            if (event.target === event.currentTarget && !ownersSaving) setOwnersModalOpen(false);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="manage-collaborators-title"
            style={{
              ...cardStyle,
              width: 'min(100%, 620px)',
              padding: 24,
              display: 'flex',
              flexDirection: 'column',
              gap: 18,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span
                id="manage-collaborators-title"
                style={{ fontSize: 20, fontWeight: 475, letterSpacing: '0.4px' }}
              >
                Manage collaborators
              </span>
              <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5, color: c.textBody }}>
                Add users or groups that can manage this app. Changes are validated against
                {' Abridge-Commercial'} and stored in the portal catalog.
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 475 }}>Owner</span>
                <Input value={app.primaryOwner ?? 'unknown'} disabled aria-label="Owner" />
                <span style={{ fontSize: 12.5, color: c.textMuted }}>
                  Transfer ownership from the Settings page. Created by {app.creator ?? 'unknown'}.
                </span>
              </label>

              <ChipInput
                label="User collaborators"
                values={ownersForm.ownerUsers}
                draft={ownerUserDraft}
                disabled={ownersSaving}
                onDraftChange={setOwnerUserDraft}
                onAdd={() => addOwnerValue('ownerUsers', ownerUserDraft)}
                onRemove={value => removeOwnerValue('ownerUsers', value)}
                suggestions={ownerUserSuggestions}
                suggestionsLoading={ownerUserSuggestionsLoading}
                suggestionsError={ownerUserSuggestionsError}
                showNoSuggestions={ownerUserDraft.trim().length >= 2}
                onSelectSuggestion={value => addOwnerValue('ownerUsers', value)}
              />

              <ChipInput
                label="Group collaborators"
                values={ownersForm.ownerGroups.filter(group => group !== 'cdp-admins')}
                draft={ownerGroupDraft}
                disabled={ownersSaving}
                onDraftChange={setOwnerGroupDraft}
                onAdd={() => addOwnerValue('ownerGroups', ownerGroupDraft)}
                onRemove={value => removeOwnerValue('ownerGroups', value)}
                suggestions={ownerGroupSuggestions.map(team => `${team.slug} (${team.name})`)}
                suggestionsLoading={ownerGroupSuggestionsLoading}
                suggestionsError={ownerGroupSuggestionsError}
                showNoSuggestions={ownerGroupDraft.trim().length >= 2}
                onSelectSuggestion={value => addOwnerValue('ownerGroups', value.split(' (')[0])}
              />
            </div>

            {ownersError && (
              <Alert variant="danger" showIcon>
                <span style={{ fontSize: 14, lineHeight: 1.35 }}>{ownersError}</span>
              </Alert>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <Button
                variant="outlined"
                size="sm"
                disabled={ownersSaving}
                onPress={() => setOwnersModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                loading={ownersSaving}
                disabled={ownersSaving || !ownersForm.owner.trim()}
                onPress={saveOwners}
              >
                Save changes
              </Button>
            </div>
          </div>
        </div>
      )}

      {iapRequestModalOpen && app && (
        <div
          role="presentation"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 50,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            background: 'rgba(21,20,19,0.48)',
          }}
          onMouseDown={event => {
            if (event.target === event.currentTarget && !iapSaving) setIapRequestModalOpen(false);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="disable-iap-title"
            style={{
              ...cardStyle,
              width: 'min(100%, 560px)',
              padding: 24,
              display: 'flex',
              flexDirection: 'column',
              gap: 18,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span id="disable-iap-title" style={{ fontSize: 20, fontWeight: 475, letterSpacing: '0.4px' }}>
                Request to disable IAP
              </span>
              <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5, color: c.textBody }}>
                This request applies to both Dev and Prod. IAP remains enabled until the platform
                team approves the pull request and the infrastructure change is applied.
              </p>
            </div>

            <label htmlFor="iap-justification" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 475 }}>Justification</span>
              <textarea
                id="iap-justification"
                value={iapJustificationDraft}
                disabled={iapSaving}
                rows={5}
                placeholder="For example, this app authenticates users directly through Okta."
                onChange={event => {
                  setIapJustificationDraft(event.target.value);
                  setIapError(null);
                }}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  resize: 'vertical',
                  border: `1px solid ${c.border}`,
                  borderRadius: 10,
                  padding: '10px 12px',
                  background: c.surface,
                  color: c.text,
                  font: 'inherit',
                  fontSize: 13.5,
                  lineHeight: 1.45,
                }}
              />
              <span style={{ fontSize: 12.5, color: c.textMuted }}>
                Explain the approved authentication method this app will use. Minimum 20 characters.
              </span>
            </label>

            {iapError && <Alert variant="danger" showIcon>{iapError}</Alert>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <Button
                variant="outlined"
                size="sm"
                disabled={iapSaving}
                onPress={() => {
                  setIapRequestModalOpen(false);
                  setIapError(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                loading={iapSaving}
                disabled={iapSaving || iapJustificationDraft.trim().length < 20}
                onPress={() => void requestIapChange(false, iapJustificationDraft)}
              >
                Open pull request
              </Button>
            </div>
          </div>
        </div>
      )}

      {accessModalOpen && app && (
        <div
          role="presentation"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 50,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            background: 'rgba(21,20,19,0.48)',
          }}
          onMouseDown={event => {
            if (event.target === event.currentTarget && !accessSaving) setAccessModalOpen(false);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-access-title"
            style={{
              ...cardStyle,
              width: 'min(100%, 620px)',
              padding: 24,
              display: 'flex',
              flexDirection: 'column',
              gap: 18,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span id="edit-access-title" style={{ fontSize: 20, fontWeight: 475, letterSpacing: '0.4px' }}>
                App access
              </span>
              <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5, color: c.textBody }}>
                {isPublic
                  ? 'Public visibility already permits all Abridge users. These separate access grants remain if the app becomes private; enable “All Abridge users” here only if that is intended.'
                  : 'Add the Abridge users who can use this application. Each person signs in with an authorized Abridge Google Workspace account.'}
              </p>
            </div>

            <AccessDelayNotice />
            <div style={{ border: `1px solid ${c.border}`, borderRadius: 12, padding: 12, background: c.subtle }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <span style={{ fontSize: 13, color: c.textMuted }}>Readiness</span>
                  <span style={{ fontSize: 13.5, color: c.textBody }}>
                    {accessReadinessLoading
                      ? 'Checking live Cloud Run service status...'
                      : 'Cloud Run service status has been checked.'}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <Tag variant={accessReadinessLoading ? 'default' : accessReadiness.dev.ready ? 'success' : 'warning'}>
                    Dev {accessReadinessLoading ? 'checking' : accessReadiness.dev.ready ? 'ready' : 'not ready'}
                  </Tag>
                  <Tag variant={accessReadinessLoading ? 'default' : accessReadiness.prod.ready ? 'success' : 'warning'}>
                    Prod {accessReadinessLoading ? 'checking' : accessReadiness.prod.ready ? 'ready' : 'not ready'}
                  </Tag>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
              <span style={{ fontSize: 13, fontWeight: 475 }}>All Abridge users</span>
              <Switch
                checked={accessForm.allAbridgeUsers}
                disabled={accessSaving}
                onCheckedChange={setAllAbridgeUsers}
                aria-label="Allow all Abridge users"
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 475 }}>Environment</span>
              <div style={{ display: 'flex', gap: 8 }}>
                {(devOnly || proposedDevOnly ? (['dev'] as const) : (['dev', 'prod', 'both'] as const)).map(environment => (
                  <Tag
                    key={environment}
                    selected={accessEnvironment === environment}
                    disabled={accessForm.allAbridgeUsers}
                    onClick={() => selectAccessEnvironment(environment)}
                  >
                    {environment === 'both' ? 'Both' : environment.toUpperCase()}
                  </Tag>
                ))}
              </div>
              {accessEnvironment === 'both' && (
                <span style={{ fontSize: 12.5, color: c.textMuted }}>
                  Both grants access to Dev and Prod and is additive.
                </span>
              )}
              {accessEnvironment === 'both' && applicationAccess.dev.join(',') !== applicationAccess.prod.join(',') && (
                <span style={{ fontSize: 12.5, color: c.textMuted }}>
                  Dev and Prod currently differ. Saving Both updates shared access in both environments.
                </span>
              )}
            </div>

            <ChipInput
              label="People who can use this app"
              values={accessForm.members}
              draft={accessDraft}
              disabled={accessSaving || accessForm.allAbridgeUsers}
              protectedValues={accessEnvironment === 'both' ? [] : accessForms.both.members}
              onDraftChange={setAccessDraft}
              onAdd={addAccessMember}
              onRemove={value => updateAccessForm(current => ({
                ...current,
                members: current.members.filter(member => member !== value),
              }))}
            />

            {accessError && (
              <Alert variant="danger" showIcon>
                <span style={{ fontSize: 14, lineHeight: 1.35 }}>{accessError}</span>
              </Alert>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <Button variant="outlined" size="sm" disabled={accessSaving} onPress={() => setAccessModalOpen(false)}>
                Cancel
              </Button>
              {!accessReadinessLoading && !accessReady && (
                <span style={{ alignSelf: 'center', marginRight: 'auto', fontSize: 12.5, color: c.textMuted }}>
                  Deploy the selected service before saving access.
                </span>
              )}
              <Button variant="primary" size="sm" loading={accessSaving} disabled={accessSaving || accessReadinessLoading || !accessReady} onPress={saveApplicationAccess}>
                Save access
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SecretsPanel({ repo, canManage, devOnly }: { repo: string; canManage: boolean; devOnly: boolean }) {
  const [environment, setEnvironment] = useState<SecretEnvironment>('dev');
  useEffect(() => {
    if (devOnly) setEnvironment('dev');
  }, [devOnly]);
  const [language, setLanguage] = useState<SecretLanguage>('typescript-javascript');
  const [snippetOpen, setSnippetOpen] = useState(false);
  const [status, setStatus] = useState<AppSecretsStatus | null>(null);
  const [secrets, setSecrets] = useState<AppSecretSummary[]>([]);
  const [envKey, setEnvKey] = useState('');
  const [value, setValue] = useState('');
  const [valueVisible, setValueVisible] = useState(false);
  const [valueEditorOpen, setValueEditorOpen] = useState(false);
  const [valueEditorVisible, setValueEditorVisible] = useState(false);
  const [drafts, setDrafts] = useState<Record<SecretEnvironment, PendingSecretDraft>>({
    dev: { updates: {}, removals: [] },
    prod: { updates: {}, removals: [] },
  });
  const [editingSecret, setEditingSecret] = useState<AppSecretSummary | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editValueVisible, setEditValueVisible] = useState(false);
  const [secretToDelete, setSecretToDelete] = useState<AppSecretSummary | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedStatus = status?.environments.find(item => item.environment === environment);
  const canEdit = canManage && Boolean(selectedStatus?.ready);
  const envKeyError = envKey && !ENV_KEY_PATTERN.test(envKey)
    ? 'Use uppercase letters, numbers, and underscores. Start with a letter.'
    : envKey.startsWith('CDP_')
      ? 'Names beginning with CDP_ are reserved for platform runtime values.'
    : envKey && secrets.some(secret => secret.envKey === envKey)
      ? 'This variable is already tracked. Use its edit pencil to replace the value.'
      : undefined;
  const draft = drafts[environment];
  const mutating = applying || deleting;
  const pendingCount = Object.keys(draft.updates).length + draft.removals.length;
  const hasPendingChanges = (devOnly ? (['dev'] as const) : (['dev', 'prod'] as const)).some(candidate =>
    Object.keys(drafts[candidate].updates).length + drafts[candidate].removals.length > 0,
  );

  const refresh = () => {
    setLoading(true);
    setError(null);
    return api
      .secretsOverview(repo, environment)
      .then(({ status: nextStatus, secrets: nextSecrets }) => {
        setStatus(nextStatus);
        nextStatus.environments.forEach(environmentStatus => {
          if (environmentStatus.message) {
            console.warn('[portal] Secret readiness check failed', {
              repo,
              environment: environmentStatus.environment,
              detail: environmentStatus.message,
            });
          }
        });
        const environmentStatus = nextStatus.environments.find(item => item.environment === environment);
        if (!environmentStatus?.ready) {
          setSecrets([]);
          return nextStatus;
        }
        setSecrets(nextSecrets);
        return nextStatus;
      })
      .catch((err: unknown) => {
        console.warn('[portal] Secrets panel request failed', { repo, detail: err });
        setError('Could not load secrets right now. Try again later.');
        return undefined;
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    let cancelled = false;
    let timeout: number | undefined;
    const poll = (): void => {
      void refresh().then(nextStatus => {
        const selectedEnvironment = nextStatus?.environments.find(item => item.environment === environment);
        if (!cancelled && !selectedEnvironment?.ready) {
          timeout = window.setTimeout(poll, 10_000);
        }
      });
    };
    poll();
    return () => {
      cancelled = true;
      if (timeout !== undefined) window.clearTimeout(timeout);
    };
  }, [repo, environment]);

  useEffect(() => {
    if (!hasPendingChanges) return;

    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [hasPendingChanges]);

  const stageUpdate = (key: string, nextValue: string) => {
    setDrafts(current => ({
      ...current,
      [environment]: {
        updates: { ...current[environment].updates, [key]: nextValue },
        removals: current[environment].removals.filter(candidate => candidate !== key),
      },
    }));
  };

  const save = () => {
    if (!ENV_KEY_PATTERN.test(envKey) || envKey.startsWith('CDP_') || !value) return;
    setError(null);
    stageUpdate(envKey, value);
    setEnvKey('');
    setValue('');
    setValueVisible(false);
    setValueEditorOpen(false);
    setValueEditorVisible(false);
  };

  const remove = (key: string) => {
    setError(null);
    const isTracked = secrets.some(secret => secret.envKey === key);
    setDrafts(current => {
      const updates = { ...current[environment].updates };
      delete updates[key];
      return {
        ...current,
        [environment]: {
          updates,
          removals: isTracked
            ? [...new Set([...current[environment].removals, key])]
            : current[environment].removals,
        },
      };
    });
  };

  const undoRemoval = (key: string) => {
    setDrafts(current => ({
      ...current,
      [environment]: {
        ...current[environment],
        removals: current[environment].removals.filter(candidate => candidate !== key),
      },
    }));
  };

  const discardUpdate = (key: string) => {
    setDrafts(current => {
      const updates = { ...current[environment].updates };
      delete updates[key];
      return { ...current, [environment]: { ...current[environment], updates } };
    });
  };

  const applyChanges = () => {
    if (pendingCount === 0) return;
    setApplying(true);
    setError(null);
    api
      .applySecretChanges(repo, {
        environment,
        updates: Object.entries(draft.updates).map(([key, nextValue]) => ({ envKey: key, value: nextValue })),
        removals: draft.removals,
      })
      .then(() => {
        setDrafts(current => ({ ...current, [environment]: { updates: {}, removals: [] } }));
        return refresh();
      })
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : 'Could not apply secret changes.'),
      )
      .finally(() => setApplying(false));
  };

  const permanentlyDelete = () => {
    if (!secretToDelete || deleteConfirmText !== secretToDelete.envKey) return;
    setDeleting(true);
    setDeleteError(null);
    api
      .deleteSecret(repo, secretToDelete.environment, secretToDelete.envKey)
      .then(() => {
        setSecretToDelete(null);
        setDeleteConfirmText('');
        return refresh();
      })
      .catch((err: unknown) =>
        setDeleteError(err instanceof Error ? err.message : 'Could not permanently delete the secret.'),
      )
      .finally(() => setDeleting(false));
  };

  const retryBinding = (secret: AppSecretSummary) => {
    setApplying(true);
    setError(null);
    api
      .retrySecretBinding(repo, secret.environment, secret.envKey)
      .then(refresh)
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : 'Could not retry the secret binding.'),
      )
      .finally(() => setApplying(false));
  };

  return (
    <div style={{ ...cardStyle, padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 18, fontWeight: 475, letterSpacing: '0.36px' }}>Secrets</span>
          <p style={{ margin: 0, color: c.textBody, fontSize: 13.5, lineHeight: 1.5, maxWidth: '70ch' }}>
            Store Secret Manager values and bind them to Cloud Run as normal environment variables. Secret values are never shown again after they are applied.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
           {(devOnly ? (['dev'] as const) : (['dev', 'prod'] as const)).map(candidate => (
            <Tag
              key={candidate}
              selected={environment === candidate}
              disabled={mutating}
              onClick={() => {
                if (!mutating) {
                  setEditingSecret(null);
                  setEditValue('');
                  setEditValueVisible(false);
                  setEnvKey('');
                  setValue('');
                  setValueVisible(false);
                  setValueEditorOpen(false);
                  setValueEditorVisible(false);
                  setEnvironment(candidate);
                }
              }}
            >
              {candidate.toUpperCase()}
              {Object.keys(drafts[candidate].updates).length + drafts[candidate].removals.length > 0
                ? ` (${Object.keys(drafts[candidate].updates).length + drafts[candidate].removals.length})`
                : ''}
            </Tag>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
        <button
          type="button"
          onClick={() => setSnippetOpen(true)}
          style={{
            padding: 0,
            border: 0,
            background: 'none',
            color: c.brand,
            fontFamily: 'inherit',
            fontSize: 13.5,
            textDecoration: 'underline',
            textUnderlineOffset: 3,
            cursor: 'pointer',
          }}
        >
          View code examples
        </button>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '5px 10px',
            border: `1px solid ${c.border}`,
            borderRadius: 999,
            background: c.subtle,
            fontSize: 12.5,
            whiteSpace: 'nowrap',
          }}
        >
          <span style={{ color: c.textMuted }}>Readiness</span>
          <Tag variant={selectedStatus?.ready ? 'success' : 'warning'} size="sm">
            {loading ? 'Checking...' : selectedStatus?.ready ? 'Ready' : 'Not ready'}
          </Tag>
        </div>
      </div>

      {!canManage && (
        <Alert variant="info" showIcon>
          <span style={{ fontSize: 14, lineHeight: 1.35 }}>
            You can view this public app, but only its owners can change settings or manage secrets.
          </span>
        </Alert>
      )}

      {canManage && selectedStatus?.ready && (
        <div className="secret-action-guide">
          <div>
            <strong>Remove binding</strong>
            <span>Use this when the app should stop receiving the value but its version history should be retained, such as for a temporary disable or planned replacement.</span>
          </div>
          <div>
            <strong>Permanently delete</strong>
            <span>Use this when the value is no longer needed. Its binding and every stored version are deleted and cannot be recovered.</span>
          </div>
        </div>
      )}

      {error && (
        <Alert variant="danger" showIcon>
          <span style={{ fontSize: 14, lineHeight: 1.35 }}>{error}</span>
        </Alert>
      )}

      {!loading && selectedStatus && !selectedStatus.ready && (
        <Alert variant="warning" showIcon>
          <span style={{ fontSize: 14, lineHeight: 1.35 }}>
            Secrets infrastructure is not ready in {environment.toUpperCase()}.
            {selectedStatus.message && <> {selectedStatus.message}</>}
          </span>
        </Alert>
      )}

      {selectedStatus?.ready && <div className="secret-entry-form">
        <FormField label="Environment variable" error={envKeyError}>
          <Input placeholder="OPENAI_API_KEY" value={envKey} onChange={event => setEnvKey(event.target.value.toUpperCase())} disabled={!canEdit || mutating} />
        </FormField>
        <FormField label="Secret value">
          <div className="secret-value-input">
            <div className="secret-value-field">
              <Input
                type={valueVisible ? 'text' : 'password'}
                autoComplete="off"
                value={value}
                onChange={event => setValue(event.target.value)}
                disabled={!canEdit || mutating}
                aria-label="Secret value"
              />
              <button type="button" className="secret-visibility-toggle"
                disabled={!canEdit || mutating}
                aria-label={`${valueVisible ? 'Hide' : 'Show'} secret value`}
                aria-pressed={valueVisible}
                title={`${valueVisible ? 'Hide' : 'Show'} secret value`}
                onClick={() => setValueVisible(current => !current)}>
                <Icon name={valueVisible ? icons.hideValue : icons.showValue} size="sm" />
              </button>
            </div>
            <Button
              variant="outlined"
              size="sm"
              leftIcon={icons.openInNew}
              disabled={!canEdit || mutating}
              aria-label="Expand secret value editor"
              onPress={() => {
                setValueEditorVisible(false);
                setValueEditorOpen(true);
              }}
            />
          </div>
        </FormField>
        <Button variant="primary" disabled={!canEdit || mutating || !value || !envKey || Boolean(envKeyError)} onPress={save}>
          Save
        </Button>
      </div>}

      {valueEditorOpen && (
        <div
          role="presentation"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 60,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            background: 'rgba(21,20,19,0.48)',
          }}
          onMouseDown={event => {
            if (event.target === event.currentTarget) {
              setValueEditorOpen(false);
              setValueEditorVisible(false);
            }
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="expanded-secret-value-title"
            style={{
              ...cardStyle,
              width: 'min(100%, 760px)',
              padding: 24,
              display: 'flex',
              flexDirection: 'column',
              gap: 20,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span id="expanded-secret-value-title" style={{ fontSize: 20, fontWeight: 475, letterSpacing: '0.4px' }}>
                Edit secret value
              </span>
              <span style={{ color: c.textMuted, fontSize: 13.5, lineHeight: 1.45 }}>
                This is the unsaved value only. It cannot be viewed again after it is applied.
              </span>
            </div>
            <FormField label="Secret value" controlId="expanded-secret-value">
              {valueEditorVisible ? (
                <textarea
                  id="expanded-secret-value"
                  className="secret-value-editor"
                  value={value}
                  autoFocus
                  autoComplete="off"
                  spellCheck={false}
                  onChange={event => setValue(event.target.value)}
                />
              ) : (
                <Input
                  id="expanded-secret-value"
                  type="password"
                  value={value}
                  autoFocus
                  autoComplete="off"
                  onChange={event => setValue(event.target.value)}
                />
              )}
            </FormField>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
              <Button
                variant="outlined"
                size="sm"
                aria-label={`${valueEditorVisible ? 'Hide' : 'Show'} expanded secret value`}
                onPress={() => setValueEditorVisible(current => !current)}
              >
                {valueEditorVisible ? 'Hide value' : 'Show value'}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onPress={() => {
                  setValueEditorOpen(false);
                  setValueEditorVisible(false);
                }}
              >
                Done
              </Button>
            </div>
          </div>
        </div>
      )}

      {editingSecret && (
        <div
          role="presentation"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 60,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            background: 'rgba(21,20,19,0.48)',
          }}
          onMouseDown={event => {
            if (event.target === event.currentTarget) {
              setEditingSecret(null);
              setEditValue('');
              setEditValueVisible(false);
            }
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-secret-title"
            style={{
              ...cardStyle,
              width: 'min(100%, 520px)',
              padding: 24,
              display: 'flex',
              flexDirection: 'column',
              gap: 20,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span id="edit-secret-title" style={{ fontSize: 20, fontWeight: 475, letterSpacing: '0.4px' }}>
                Update {editingSecret.envKey}
              </span>
              <span style={{ color: c.textMuted, fontSize: 13.5, lineHeight: 1.45 }}>
                Enter a replacement value. The existing value cannot be shown.
              </span>
            </div>
            <FormField label="New secret value" helperText="A new Secret Manager version is created when changes are applied.">
              <div className="secret-value-input">
                <Input
                  type={editValueVisible ? 'text' : 'password'}
                  autoComplete="off"
                  value={editValue}
                  autoFocus
                  onChange={event => setEditValue(event.target.value)}
                />
                <Button
                  variant="outlined"
                  size="sm"
                  aria-label={`${editValueVisible ? 'Hide' : 'Show'} new secret value`}
                  onPress={() => setEditValueVisible(current => !current)}
                >
                  {editValueVisible ? 'Hide' : 'Show'}
                </Button>
              </div>
            </FormField>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <Button variant="outlined" size="sm" onPress={() => {
                setEditingSecret(null);
                setEditValue('');
                setEditValueVisible(false);
              }}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                disabled={!editValue}
                onPress={() => {
                  stageUpdate(editingSecret.envKey, editValue);
                  setEditingSecret(null);
                  setEditValue('');
                  setEditValueVisible(false);
                }}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      )}

      {secretToDelete && (
        <div
          role="presentation"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 60,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            background: 'rgba(21,20,19,0.48)',
          }}
          onMouseDown={event => {
            if (event.target === event.currentTarget && !deleting) setSecretToDelete(null);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-secret-title"
            style={{
              ...cardStyle,
              width: 'min(100%, 540px)',
              padding: 24,
              display: 'flex',
              flexDirection: 'column',
              gap: 18,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span id="delete-secret-title" style={{ fontSize: 20, fontWeight: 475, letterSpacing: '0.4px' }}>
                Delete {secretToDelete.envKey}?
              </span>
              <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5, color: c.textBody }}>
                This removes the {secretToDelete.environment.toUpperCase()} Cloud Run binding and permanently deletes every stored version from Secret Manager.
              </p>
            </div>

            <Alert variant="warning" showIcon>
              <span style={{ fontSize: 14, lineHeight: 1.4 }}>
                Older revisions that reference this secret may no longer restart or roll back successfully. To confirm, type <strong>{secretToDelete.envKey}</strong> below.
              </span>
            </Alert>

            <Input
              value={deleteConfirmText}
              onChange={event => setDeleteConfirmText(event.target.value)}
              placeholder={secretToDelete.envKey}
              disabled={deleting}
              aria-label={`Type ${secretToDelete.envKey} to confirm deletion`}
              autoFocus
            />

            {deleteError && (
              <Alert variant="danger" showIcon>
                <span style={{ fontSize: 14, lineHeight: 1.35 }}>{deleteError}</span>
              </Alert>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <Button variant="outlined" size="sm" disabled={deleting} onPress={() => setSecretToDelete(null)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                size="sm"
                loading={deleting}
                disabled={deleting || deleteConfirmText !== secretToDelete.envKey}
                onPress={permanentlyDelete}
              >
                Permanently delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {snippetOpen && (
        <div
          role="presentation"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 50,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            background: 'rgba(21,20,19,0.48)',
          }}
          onMouseDown={event => {
            if (event.target === event.currentTarget) setSnippetOpen(false);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="secret-code-examples-title"
            style={{
              ...cardStyle,
              width: 'min(100%, 760px)',
              padding: 24,
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'center' }}>
              <span id="secret-code-examples-title" style={{ fontSize: 18, fontWeight: 475 }}>
                Use your secret in code
              </span>
              <Button variant="outlined" size="sm" onPress={() => setSnippetOpen(false)}>
                Close
              </Button>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {SECRET_LANGUAGES.map(candidate => (
                <Tag key={candidate} selected={language === candidate} onClick={() => setLanguage(candidate)}>
                  {candidate === 'typescript-javascript' ? 'TypeScript / JavaScript' : candidate === 'python' ? 'Python' : 'Go'}
                </Tag>
              ))}
            </div>
            <p style={{ margin: 0, color: c.textBody, fontSize: 13.5, lineHeight: 1.5 }}>
              Add a secret with the name <code style={{ fontFamily: mono }}>OPENAI_API_KEY</code>, then read it from the environment in your app:
            </p>
            <pre
              style={{
                margin: 0,
                padding: 16,
                overflowX: 'auto',
                borderRadius: 12,
                background: c.subtle,
                border: `1px solid ${c.border}`,
                color: c.text,
                fontFamily: mono,
                fontSize: 13,
                lineHeight: 1.55,
              }}
            >
              {SECRET_SNIPPETS[language]}
            </pre>
          </div>
        </div>
      )}

      {selectedStatus?.ready && <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {secrets.length === 0 && Object.keys(draft.updates).length === 0 ? (
          <div style={{ border: `1px dashed ${c.border}`, borderRadius: 14, padding: 20, color: c.textFaint, fontSize: 13.5 }}>
            No portal-managed secrets exist in {environment.toUpperCase()} yet.
          </div>
        ) : (
          <>
            {secrets.map(secret => {
              const updatePending = Boolean(draft.updates[secret.envKey]);
              const removalPending = draft.removals.includes(secret.envKey);
              const bindingState = secret.bindingState ?? (secret.boundToCloudRun ? 'bound' : 'unbound');
              const latestVersionIsBound = bindingState === 'bound';
              const hasAnyBinding = bindingState !== 'unbound';
              return (
                <div key={secret.envKey} className="secret-binding-row" style={{ border: `1px solid ${c.border}`, borderRadius: 14, padding: 14, opacity: removalPending ? 0.65 : 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontFamily: mono, fontSize: 13 }}>{secret.envKey}</span>
                    <Button
                      variant="icon"
                      size="sm"
                      disabled={!canEdit || mutating || removalPending}
                      aria-label={`Edit ${secret.envKey}`}
                      onPress={() => {
                        setEditValue('');
                        setEditValueVisible(false);
                        setEditingSecret(secret);
                      }}
                    >
                      <Icon name={icons.edit} size="sm" color={c.textMuted} />
                    </Button>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: mono, fontSize: 12, color: c.textMuted, overflow: 'hidden', textOverflow: 'ellipsis' }}>{secret.secretId}</span>
                    {updatePending && <Tag variant="info" size="sm">Update pending</Tag>}
                    {removalPending && <Tag variant="warning" size="sm">Removal pending</Tag>}
                    {bindingState === 'unbound' && !updatePending && <Tag variant="warning" size="sm">Unbound</Tag>}
                    {bindingState === 'partial' && !updatePending && <Tag variant="warning" size="sm">Partially bound</Tag>}
                    {updatePending && (
                      <Button variant="tertiary" size="sm" disabled={applying} onPress={() => discardUpdate(secret.envKey)}>
                        Undo update
                      </Button>
                    )}
                  </div>
                  <div className="secret-binding-actions">
                    {hasAnyBinding && (
                      <Button
                        variant="outlined"
                        size="sm"
                        disabled={!canEdit || mutating}
                        onPress={() => removalPending ? undoRemoval(secret.envKey) : remove(secret.envKey)}
                      >
                        {removalPending ? 'Undo' : 'Remove binding'}
                      </Button>
                    )}
                    {!latestVersionIsBound && (
                      <Button
                        variant="outlined"
                        size="sm"
                        disabled={!canEdit || mutating || updatePending}
                        onPress={() => retryBinding(secret)}
                      >
                        Retry binding
                      </Button>
                    )}
                    <Button
                      variant="danger"
                      size="sm"
                      disabled={!canEdit || mutating || removalPending || updatePending}
                      onPress={() => {
                        setDeleteConfirmText('');
                        setDeleteError(null);
                        setSecretToDelete(secret);
                      }}
                    >
                      Delete secret
                    </Button>
                  </div>
                </div>
              );
            })}
            {Object.keys(draft.updates)
              .filter(key => !secrets.some(secret => secret.envKey === key))
              .map(key => (
                <div key={key} className="secret-binding-row" style={{ border: `1px solid ${c.border}`, borderRadius: 14, padding: 14 }}>
                  <span style={{ fontFamily: mono, fontSize: 13 }}>{key}</span>
                  <Tag variant="info" size="sm" style={{ justifySelf: 'start' }}>New secret pending</Tag>
                  <Button variant="outlined" size="sm" disabled={applying} onPress={() => discardUpdate(key)}>
                    Discard
                  </Button>
                </div>
              ))}
          </>
        )}
      </div>}

      {selectedStatus?.ready && pendingCount > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 16, flexWrap: 'wrap', paddingTop: 4 }}>
          <span style={{ color: c.textMuted, fontSize: 13.5 }}>
            Staged until you apply all changes.
          </span>
          <Button variant="primary" loading={applying} disabled={mutating} onPress={applyChanges}>
            Apply changes
          </Button>
        </div>
      )}
    </div>
  );
}

function ChipInput({
  label,
  values,
  draft,
  disabled,
  onDraftChange,
  onAdd,
  onRemove,
  suggestions = [],
  suggestionsLoading = false,
  suggestionsError = null,
  showNoSuggestions = false,
  onSelectSuggestion,
  protectedValues = [],
}: {
  label: string;
  values: string[];
  draft: string;
  disabled: boolean;
  onDraftChange: (value: string) => void;
  onAdd: () => void;
  onRemove: (value: string) => void;
  suggestions?: string[];
  suggestionsLoading?: boolean;
  suggestionsError?: string | null;
  showNoSuggestions?: boolean;
  onSelectSuggestion?: (value: string) => void;
  protectedValues?: string[];
}) {
  const inputId = useId();
  const submitDraft = (): void => {
    if (!draft.trim()) return;
    onAdd();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <label htmlFor={inputId} style={{ fontSize: 13, fontWeight: 475 }}>{label}</label>
      {values.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {values.map(value => (
            <span
              key={value}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                borderRadius: 999,
                padding: '5px 8px 5px 10px',
                background: c.subtle,
                border: `1px solid ${c.border}`,
                fontSize: 13,
              }}
            >
              {value}
              <button
                type="button"
                disabled={disabled || protectedValues.includes(value)}
                aria-label={`Remove ${value}`}
                onClick={event => {
                  event.stopPropagation();
                  onRemove(value);
                }}
                style={{
                  border: 0,
                  background: 'transparent',
                  color: c.textMuted,
                  cursor: disabled || protectedValues.includes(value) ? 'not-allowed' : 'pointer',
                  font: 'inherit',
                  padding: 0,
                  lineHeight: 1,
                }}
              >
                x
              </button>
            </span>
          ))}
        </div>
      )}
      <div style={{ position: 'relative', display: 'flex', gap: 8 }}>
        <div style={{ flex: '1 1 auto' }}>
          <Input
            id={inputId}
            value={draft}
            onChange={event => onDraftChange(event.target.value)}
            disabled={disabled}
          />
        </div>
        <Button
          variant="outlined"
          size="sm"
          disabled={disabled || !draft.trim()}
          onPress={submitDraft}
          style={{ flex: '0 0 auto' }}
        >
          Add
        </Button>
        {(suggestionsLoading || suggestions.length > 0 || showNoSuggestions) && onSelectSuggestion && (
          <div
            style={{
              position: 'absolute',
              zIndex: 70,
              top: 'calc(100% + 4px)',
              left: 0,
              right: 76,
              border: `1px solid ${c.border}`,
              borderRadius: 12,
              background: c.surface,
              boxShadow: '0 14px 34px rgba(21,20,19,0.14)',
              overflow: 'hidden',
            }}
          >
            {suggestionsLoading ? (
              <div style={{ padding: '10px 12px', fontSize: 13, color: c.textMuted }}>Searching...</div>
            ) : suggestions.length === 0 ? (
              <div style={{ padding: '10px 12px', fontSize: 13, color: c.textMuted }}>
                No matching org members
              </div>
            ) : (
              suggestions.map(suggestion => (
                <button
                  key={suggestion}
                  type="button"
                  onClick={() => onSelectSuggestion(suggestion)}
                  style={{
                    display: 'block',
                    width: '100%',
                    border: 0,
                    background: 'transparent',
                    padding: '10px 12px',
                    textAlign: 'left',
                    font: 'inherit',
                    fontSize: 13,
                    color: c.text,
                    cursor: 'pointer',
                  }}
                >
                  {suggestion}
                </button>
              ))
            )}
          </div>
        )}
      </div>
      {suggestionsError && (
        <span style={{ fontSize: 12.5, color: c.brand }}>{suggestionsError}</span>
      )}
    </div>
  );
}
